// ═══════════════════════════════════════════════════════════════
// PIXALLOY — Main Application Component
// ═══════════════════════════════════════════════════════════════
// HostBridge integration (Step 3 — full worker mode):
//   - HostBridge owns the engine tick loop via initWorker() or initWithWasm()
//   - ParamWriter (bridge.setParam) replaces PB
//   - RingProducer (bridge.pushCommand/pushSimple) for simple commands
//   - Sideband MessageChannel (bridge.pushSideband) for complex commands
//   - Overlay canvas rAF remains on the main thread
//   - In worker mode: selection mask, transform data, layer sync via postMessage
//   - In single-thread fallback: direct engine access still works
// ═══════════════════════════════════════════════════════════════
import { useState, useRef, useEffect, useCallback, useReducer } from "react";
import { V, optInputStyle } from "./theme.js";
import {
  C, T, FLT, ADJ, ADJ_DEFAULTS, ADJ_LABELS, BLEND_MAP, BLENDS,
  CURVE_PRESETS, SWATCHES, FILTERS, EXPORTS, ACCEPT, ANCHORS,
  mkL, mkAdjL, mkGroupL, mkEngine, applyAdjustment,
} from "./engine/index.js";
import { TOOLS, CURS, KEY_MAP, RADIAL_ITEMS } from "./tools/toolDefs.jsx";
import {
  penBounds, penApplyTransform, genShape, drawPenPath, drawPathCurve,
  mkPt, mkPath, pathBounds, pathListBounds, pathListToFlat,
  hitTestPoints, totalPointCount, closePath, transformPathList,
} from "./tools/penTool.js";
import { ants, antsPreview, tfmH, bCur } from "./ui/overlays.js";
import { detectCapabilities } from "./wasm/index.js";
import {
  hx, hx2, rgbHex, rgbToHsl,
  OptSep, OptBtn, LayerAction, PanelSection, InfoCell, AdjRow, Modal,
  LayerThumb, MaskThumb, ToolIcon,
} from "./ui/components.jsx";
import { HostBridge, RB_DIRTY, RB_HAS_SELECTION, RB_SEL_X, RB_SEL_Y, RB_SEL_W, RB_SEL_H, RB_HAS_TRANSFORM, RB_TFM_DATA, RB_STROKE_ON, RB_LAYER_COUNT, RB_ACTIVE_LAYER } from "@alloyhead/core";
import { PIXALLOY_SCHEMA } from "../src/schema.ts";
import { createPixAlloyAdapter, BLEND_LABEL_TO_IDX, RANGE_MAP } from "../src/adapter.ts";

// ── Dialog components (extracted from PixAlloy.jsx for maintainability) ──
import { TextDialog }       from "./dialogs/TextDialog.jsx";
import { FxDialog }         from "./dialogs/FxDialog.jsx";
import { AdjustmentDialog } from "./dialogs/AdjustmentDialog.jsx";
import {
  LevelsDialog, HueSatDialog, GaussBlurDialog, ColorBalDialog,
  ImageSizeDialog, CanvasSizeDialog,
  AboutDialog, GroupDeleteDialog, SaveGuardDialog,
} from "./dialogs/FilterDialogs.jsx";
import { Splash } from "./Splash.jsx";
import { useKeyboard } from "./hooks/useKeyboard.js";
import { importPSD } from "./psd-import.js";
import { parseVectorFile, pathListToSvg } from "./tools/vectorImport.js";
import { VectorStyleDialog } from "./dialogs/VectorStyleDialog.jsx";
import { ExportDialog } from "./dialogs/ExportDialog.jsx";
import { ActionsDialog, useActions } from "./dialogs/ActionsDialog.jsx";
import { useAutoSave } from "./hooks/useAutoSave.js";
import { DevelopDialog } from "./dialogs/DevelopDialog.jsx";
import { exportPSD } from "./psd-export.js";
import { renderToolbar } from "./sections/Toolbar.jsx";
import { renderMenuBar } from "./sections/MenuBar.jsx";
import { renderOptionsBar } from "./sections/OptionsBar.jsx";
import { renderLeftPanel } from "./sections/LeftPanel.jsx";
import { renderCanvasArea } from "./sections/CanvasArea.jsx";
import { renderRightPanel } from "./sections/RightPanel.jsx";
import { usePointer } from "./hooks/usePointer.js";
import { useResponsive } from "./hooks/useResponsive.js";
import { useTouchGestures } from "./hooks/useTouchGestures.js";
import { useBrushState } from "./hooks/useBrushState.js";

// Worker URL — Vite resolves this to a bundled worker script
const WORKER_URL = new URL("../src/worker.ts", import.meta.url);
const ADAPTER_FACTORY_URL = new URL("../src/adapter-factory.ts", import.meta.url).href;

export default function PixAlloy(){
  const cvs=useRef(null),eng=useRef(null),raf=useRef(null),ao=useRef(0);
  const bridgeRef=useRef(null); // HostBridge instance
  const sidebandRef=useRef(null); // Sideband API for complex-payload commands (single-thread only)
  const readbackViewRef=useRef(null); // DataView over readback SAB for overlay reads
  const layerSyncRef=useRef(null); // Cached layer list from onLayerSync callback
  const workerModeRef=useRef(false); // true when running in worker mode
  const selMaskRef=useRef(null); // Cached selection mask from worker (Uint8Array + bounds)
  const tfmBitmapRef=useRef(null); // Cached transform preview bitmap from worker
  const[wasmCaps,setWasmCaps]=useState(null); // {sharedMemory, worker, wasm, ...}
  const cvsCtxRef=useRef(null); // cached 2d context (single-thread only)
  const ovlRef=useRef(null); // overlay canvas ref
  const ovlCtxRef=useRef(null); // overlay canvas 2d context
  const cvsRefCb=useCallback(el=>{
    cvs.current=el;
    // Do NOT eagerly create 2D context here — in worker mode the canvas
    // must be transferred to the worker via transferControlToOffscreen(),
    // which fails if a context already exists. The context is created
    // lazily in initEngine() for single-thread mode only.
  },[]);
  const ovlRefCb=useCallback(el=>{
    ovlRef.current=el;
    if(el){ovlCtxRef.current=el.getContext("2d");}else{ovlCtxRef.current=null;}
  },[]);
  const ss=useRef(null),sd=useRef(null),fi=useRef(null),drag=useRef(null),toolR=useRef(T.BRUSH);const lassoRef=useRef([]);const polyRef=useRef([]);
  const bgColRef=useRef("#ffffff");
  const rKeyHeldRef=useRef(false); // Track R key for canvas rotation
  const spaceHeldRef=useRef(false); // Track Space key for temporary pan (1.2)
  const toolBeforeSpaceRef=useRef(null); // Restore tool after spacebar pan (1.2)
  const navCvs=useRef(null),histCvs=useRef(null),waveCvs=useRef(null);
  const fpsRef=useRef({count:0,lastTime:performance.now(),fps:0,tickMs:0});
  const vpRef=useRef(null);
  const vpContainerRef=useRef(null);
  const panState=useRef({zoom:1,panX:0,panY:0,isPanning:false,lastMX:0,lastMY:0,rotation:0,isRotating:false});

  const[showSplash,setShowSplash]=useState(true);
  const[mobileGateDismissed,setMobileGateDismissed]=useState(()=>typeof sessionStorage!=="undefined"&&sessionStorage.getItem("pixalloy_mobile_ok")==="1");
  const[sessionId,setSessionId]=useState(0); // Incremented on New Document to force canvas remount (enables worker mode)
  const[splashW,setSplashW]=useState(1200),[splashH,setSplashH]=useState(800);
  const[tool,_setTool]=useState(T.BRUSH);
  // Brush state consolidated into hook (Refactor 3)
  const brush = useBrushState(bridgeRef);
  const { bsz, setBsz, bop, setBop, bhard, setBhard, btip, setBtip,
    bcol, setBcol, bgCol, setBgCol, bcolRef,
    pressureSize, setPressureSize, pressureOpacity, setPressureOpacity,
    pressureHardness, setPressureHardness, pressureFlow, setPressureFlow,
    pressureCurve, setPressureCurve,
    brushSpacing, setBrushSpacing, brushFlow, setBrushFlow,
    sizeJitter, setSizeJitter, angleJitter, setAngleJitter,
    opacityJitter, setOpacityJitter, scatterAmount, setScatterAmount,
    brushSmoothing, setBrushSmoothing } = brush;
  const[fillMode,setFillMode_]=useState("solid");const fillModeRef=useRef("solid");const setFillMode=v=>{setFillMode_(v);fillModeRef.current=v;};
  const[fillCol2,setFillCol2_]=useState("#000000");const fillCol2Ref=useRef("#000000");const setFillCol2=v=>{setFillCol2_(v);fillCol2Ref.current=v;};
  // Gradient tool state
  const[gradMode,setGradMode_]=useState("gradient-linear");const gradModeRef=useRef("gradient-linear");const setGradMode=v=>{setGradMode_(v);gradModeRef.current=v;};
  const defaultStops=[{pos:0,color:"#841116"},{pos:1,color:"#000000"}];
  const[gradStops,setGradStops_]=useState(defaultStops);const gradStopsRef=useRef(defaultStops);const setGradStops=v=>{const nv=typeof v==="function"?v(gradStopsRef.current):v;gradStopsRef.current=nv;setGradStops_(nv);};
  // Gaussian Blur dialog
  const[showGaussBlur,setShowGaussBlur]=useState(false);
  const[showColorBal,setShowColorBal]=useState(false);
  const[renamingLayer,setRenamingLayer]=useState(null); // layer index being renamed, or null
  const[renameText,setRenameText]=useState("");
  // v0.7: Eyedropper sample size
  const[eyedropSample,setEyedropSample_]=useState(1);const eyedropSampleRef=useRef(1);const setEyedropSample=v=>{setEyedropSample_(v);eyedropSampleRef.current=v;};
  // v0.7: Canvas rotation display
  const[rotDisplay,setRotDisplay]=useState(0);
  // v0.7: Group delete confirmation
  const[groupDelConfirm,setGroupDelConfirm]=useState(null); // null or {idx, name, childCount}
  // v0.9: Pen tool — PathList model (Path[] where Path = { id, points, closed, name })
  const penPathRef=useRef([]); // PathList: array of Path objects
  const[penPath,setPenPath_]=useState([]);const setPenPath=v=>{const nv=typeof v==="function"?v(penPathRef.current):v;penPathRef.current=nv;setPenPath_(nv);};
  const penDragRef=useRef(null);
  const penUndoRef=useRef([]); // pen path undo stack (stores PathList snapshots)
  const penRedoRef=useRef([]); // pen path redo stack
  const[activePathIdx,setActivePathIdx_]=useState(0); // index of the active path in the PathList
  const activePathIdxRef=useRef(0);const setActivePathIdx=v=>{const nv=typeof v==="function"?v(activePathIdxRef.current):v;activePathIdxRef.current=nv;setActivePathIdx_(nv);};
  // Wrapper that saves pen undo state before changes
  const setPenPathWithUndo=(v)=>{
    penUndoRef.current.push(JSON.parse(JSON.stringify(penPathRef.current)));
    if(penUndoRef.current.length>30)penUndoRef.current.shift();
    penRedoRef.current=[];
    const nv=typeof v==="function"?v(penPathRef.current):v;
    penPathRef.current=nv;setPenPath_(nv);
  };
  const penUndo=()=>{
    if(penUndoRef.current.length===0)return;
    penRedoRef.current.push(JSON.parse(JSON.stringify(penPathRef.current)));
    const prev=penUndoRef.current.pop();
    penPathRef.current=prev;setPenPath_(prev);
  };
  const penRedo=()=>{
    if(penRedoRef.current.length===0)return;
    penUndoRef.current.push(JSON.parse(JSON.stringify(penPathRef.current)));
    const next=penRedoRef.current.pop();
    penPathRef.current=next;setPenPath_(next);
  };
  const[penPreset,setPenPreset_]=useState("none");const penPresetRef=useRef("none");const setPenPreset=v=>{setPenPreset_(v);penPresetRef.current=v;};
  const[penHover,setPenHover]=useState(false); // hovering over a pen anchor/handle
  const[tfmCursor,setTfmCursor]=useState(null); // cursor override when hovering transform handles
  const[penStrokeSize,setPenStrokeSize]=useState(4); // stroke path thickness
  const[cw,setCw]=useState(1200),[ch,setCh]=useState(800);
  const[bright,setBright]=useState(0),[contrast,setContrast]=useState(0),[sat,setSat]=useState(0);
  const[showText,setShowText]=useState(false);
  const textSeedRef=useRef(null); // {x,y} for new text, or full textData object for edit
  const[curPos,setCurPos]=useState({x:0,y:0});
  const[curRGB,setCurRGB]=useState({r:0,g:0,b:0});
  const[histCh,setHistCh]=useState("rgb");
  const histChRef=useRef("rgb");
  const[activeMenu,setActiveMenu]=useState(null);
  const[showAbout,setShowAbout]=useState(false);
  // Save guard: replaces confirm() which is blocked in artifact sandbox
  const[saveGuard,setSaveGuard]=useState(null); // null or {action: "new"|"open", bmp?: ImageBitmap}
  const[showLevels,setShowLevels]=useState(false);
  const[showAdjDialog,setShowAdjDialog]=useState(false);
  const[adjEditIdx,setAdjEditIdx]=useState(null);   // null = adding new, number = editing existing
  const[adjEditType,setAdjEditType]=useState(ADJ.BRIGHTNESS);
  const[showFx,setShowFx]=useState(false);
  const[fxLayer,setFxLayer]=useState(0);
  const[showImgSize,setShowImgSize]=useState(false);
  const[showCanvasSize,setShowCanvasSize]=useState(false);
  const[showVectorStyle,setShowVectorStyle]=useState(false);
  const[vectorStyleLayer,setVectorStyleLayer]=useState(null); // layer index for vector style dialog
  const[histJumpIdx,setHistJumpIdx]=useState(null); // visual indicator of current position
  // Phase 8: Color Range selection mode
  const[colorRangeMode,setColorRangeMode]=useState(false);
  const[colorRangeFuzz,setColorRangeFuzz]=useState(30);
  // Phase 9: Export dialog, actions, auto-save
  const[showExportDialog,setShowExportDialog]=useState(false);
  const[showActionsDialog,setShowActionsDialog]=useState(false);
  const actionsHook=useActions();
  // Destructive Hue/Sat filter dialog
  const[showHueSat,setShowHueSat]=useState(false);
  const fiModeRef=useRef('open'); // 'open' | 'layer'
  const dirtyRef=useRef(false); // true when document has unsaved changes
  const autoSave=useAutoSave(eng,dirtyRef);
  const[showDevelop,setShowDevelop]=useState(false);
  const initCleanRef=useRef(false); // suppress first dirty readback from init FILL_COLOR
  // Hidden layer warning throttle (1.4)
  const lastHiddenWarnRef=useRef(0);
  // Busy cursor for long operations (1.6)
  const[busy,setBusy]=useState(false);
  const[pendingOpen,setPendingOpen]=useState(false); // triggers file dialog after splash dismisses
  const[collapsedSections,setCollapsedSections]=useState({});
  const[toast,setToast]=useState(null); // null or {msg, type:'error'|'info', id}
  const toastTimer=useRef(null);
  const[showDevOverlay,setShowDevOverlay]=useState(false); // F3 perf telemetry overlay
  const[layerBlend,setLayerBlend]=useState("Normal");
  const[layerOp,setLayerOp]=useState(100);
  const[wandTol,setWandTol_]=useState(30);const wandTolRef=useRef(30);const setWandTol=v=>{setWandTol_(v);wandTolRef.current=v;};
  const[featherR,setFeatherR]=useState(5);
  const[leftW,setLeftW]=useState(220),[rightW,setRightW]=useState(270);
  const[leftCollapsed,setLeftCollapsed]=useState(false),[rightCollapsed,setRightCollapsed]=useState(false);
  const[showClip,setShowClip]=useState(false),[showWave,setShowWave]=useState(false);
  const showWaveRef=useRef(false);
  const[radial,setRadial]=useState(null);
  const[selCtxMenu,setSelCtxMenu]=useState(null); // {x, y} for selection right-click menu
  const[zmDisplay,setZmDisplay]=useState(100);
  const[layerDrag,setLayerDrag]=useState(null); // {fromIdx, overIdx}
  const[altHeld,setAltHeld]=useState(false);
  const[showRulers,setShowRulers]=useState(true);
  const[snapEnabled,setSnapEnabled]=useState(true);
  const[guides,setGuides_]=useState([]); // [{type:"h"|"v", pos: number}]
  const guidesRef=useRef([]);
  const setGuides=(v)=>{const nv=typeof v==='function'?v(guidesRef.current):v;guidesRef.current=nv;setGuides_(nv);};
  const guideDragRef=useRef(null); // {type:"h"|"v", pos:number} during drag from ruler
  const[nearGuide,setNearGuide]=useState(null); // "h"|"v"|null when Move tool hovers near a guide
  const[,fu]=useReducer(x=>x+1,0);

  const applyVPTransform=useCallback(()=>{
    const el=vpRef.current;if(!el)return;
    const{zoom,panX,panY,rotation}=panState.current;
    el.style.transform=`translate(${panX}px,${panY}px) scale(${zoom}) rotate(${rotation}deg)`;
    setZmDisplay(Math.round(zoom*100));
  },[]);

  // v0.8: Zoom to fit canvas in viewport
  const zoomToFit=useCallback(()=>{
    const container=vpContainerRef.current;if(!container)return;
    const rect=container.getBoundingClientRect();
    const padW=rect.width-40,padH=rect.height-40; // 20px padding each side
    if(padW<1||padH<1)return;
    const fitZoom=Math.min(padW/cw,padH/ch,4);
    panState.current.zoom=fitZoom;
    panState.current.panX=0;
    panState.current.panY=0;
    applyVPTransform();
  },[cw,ch,applyVPTransform]);

  // Init engine — called when splash is dismissed or on mount if no splash
  // Detects capabilities and chooses worker mode (off main thread) or
  // single-thread fallback. Worker mode requires SharedArrayBuffer + OffscreenCanvas.
  // forceMode: 'worker' | 'single' | null — override auto-detection
  const initEngine=useCallback(async(w2,h2,forceMode)=>{
    const bridgeCallbacks={
      onFrame:(stats)=>{fpsRef.current.fps=stats.fps;fpsRef.current.tickMs=stats.tickMs;},
      onReady:()=>console.log("[PixAlloy] HostBridge ready"),
      onError:(code,msg)=>{
        console.error("[PixAlloy] Bridge error:",code,msg);
        if(toastTimer.current)clearTimeout(toastTimer.current);
        setToast({msg:`Error ${code}: ${msg}`,type:"error",id:Date.now()});
        toastTimer.current=setTimeout(()=>setToast(null),6000);
      },
      onLayerSync:(layers)=>{layerSyncRef.current=layers;},
    };

    const canvas=cvs.current;
    if(!canvas)throw new Error("Canvas ref not available for HostBridge init");

    // Can we use worker mode?
    const caps=detectCapabilities();
    const canTransfer=!cvsCtxRef.current;
    const canWorker=forceMode!=="single"&&canTransfer&&typeof SharedArrayBuffer!=="undefined"&&typeof OffscreenCanvas!=="undefined"&&caps.wasm;

    if(canWorker){
      // ══════════ WORKER MODE (zero-config via adapter-factory) ══════════
      // The framework auto-detects worker prerequisites and spawns the worker
      // internally. The adapter-factory module is dynamically imported inside
      // the worker — no custom worker script needed.
      workerModeRef.current=true;
      const bridge=new HostBridge(PIXALLOY_SCHEMA,{},bridgeCallbacks);
      bridge.setDimsChangedCallback((nw,nh)=>{
        setCw(nw);setCh(nh);
        if(ovlRef.current){ovlRef.current.width=nw;ovlRef.current.height=nh;ovlCtxRef.current=null;}
      });
      canvas.width=w2;canvas.height=h2;
      setCw(w2);setCh(h2);

      // initWithWasm with adapterUrl triggers auto-detect worker mode:
      // SAB + OffscreenCanvas available → worker mode
      // Otherwise → single-thread fallback
      const dummyAdapter={pushParams:()=>{},pushCommand:()=>{},tick:()=>0};
      await bridge.initWithWasm(canvas,dummyAdapter,{adapterUrl:ADAPTER_FACTORY_URL,forceMode:'worker'});
      bridgeRef.current=bridge;
      readbackViewRef.current=bridge.getReadbackView();

      const defaults={brightness:0,contrast:0,saturation:0,brushSize:12,brushOpacity:1,brushR:132,brushG:17,brushB:22,cursorX:-99,cursorY:-99,cursorDown:0,activeTool:0,activeLayer:0,pressure:1,pressureSize:1,pressureOpacity:0,brushSpacing:10,brushFlow:1,sizeJitter:0,angleJitter:0,opacityJitter:0,scatterAmount:0,brushSmoothing:0,pressureHardness:0,pressureFlow:0,pressureCurve:1,tiltX:0,tiltY:0};
      for(const[k,v]of Object.entries(defaults)){bridge.setParam(k,v);}
      // Wait for the worker's adapter to finish its async init (Wasm load,
      // engine create, hook wiring) before starting the tick loop.
      // Commands pushed before adapter-ready are silently dropped.
      await bridge.waitForAdapter();
      bridge.start();
      // Trigger the first layer-sync from the worker so layerSyncRef.current gets
      // populated with the Background layer before any user interaction.
      // Without this, layers=[] in React state and tool handlers are blocked.
      // FILL_COLOR white: calls pU() inside the engine → sets dirty → worker's
      // postTickHook sends layer-sync → onLayerSync → layerSyncRef populated → fu().
      bridge.pushSideband(C.FILL_COLOR,{r:255,g:255,b:255,label:'init'});
      eng.current=null;
      sidebandRef.current=null;
      dirtyRef.current=false;
      // The init FILL_COLOR triggers s.dirty in the worker (via pU), which the overlay
      // tick reads and sets dirtyRef.current=true — falsely marking a brand-new document
      // as having unsaved changes. Suppress the first dirty readback after init.
      initCleanRef.current=true;
      console.log("[PixAlloy] Worker mode started");
    }else{
      // ══════════ SINGLE-THREAD ══════════
      workerModeRef.current=false;
      canvas.width=w2;canvas.height=h2;
      if(!cvsCtxRef.current){cvsCtxRef.current=canvas.getContext("2d");}

      const{ adapter, engine, wasmBridge: wb, sideband }=await createPixAlloyAdapter(w2,h2);
      eng.current=engine;
      sidebandRef.current=sideband;
      setWasmCaps(wb.capabilities);

      engine.s.onDimsChanged=(nw,nh)=>{setCw(nw);setCh(nh);bridgeRef.current?.resize(nw,nh,true);if(ovlRef.current){ovlRef.current.width=nw;ovlRef.current.height=nh;ovlCtxRef.current=null;}};
      // Hidden layer warning (1.4) — throttled to once per 2 seconds
      engine.s.onHiddenLayerAttempt=()=>{
        const now=Date.now();
        if(now-lastHiddenWarnRef.current<2000)return;
        lastHiddenWarnRef.current=now;
        setToast({msg:"Cannot edit — layer is hidden. Click 👁 to show.",type:"info",id:now});
        if(toastTimer.current)clearTimeout(toastTimer.current);
        toastTimer.current=setTimeout(()=>setToast(null),3000);
      };
      setCw(w2);setCh(h2);
      drawDefaultImage(engine);
      dirtyRef.current=false;

      const bridge=new HostBridge(PIXALLOY_SCHEMA,{},bridgeCallbacks);
      await bridge.initWithWasm(canvas,adapter);
      bridgeRef.current=bridge;
      readbackViewRef.current=bridge.getReadbackView();

      const defaults={brightness:0,contrast:0,saturation:0,brushSize:12,brushOpacity:1,brushR:132,brushG:17,brushB:22,cursorX:-99,cursorY:-99,cursorDown:0,activeTool:0,activeLayer:0,pressure:1,pressureSize:1,pressureOpacity:0,brushSpacing:10,brushFlow:1,sizeJitter:0,angleJitter:0,opacityJitter:0,scatterAmount:0,brushSmoothing:0,pressureHardness:0,pressureFlow:0,pressureCurve:1,tiltX:0,tiltY:0};
      for(const[k,v]of Object.entries(defaults)){bridge.setParam(k,v);}
      bridge.start();

      // Circuit breaker recovery — if the engine crashes 3 times, the tick loop
      // calls this factory to create a fresh engine instance.
      bridge.setEngineFactory(()=>{
        console.warn("[PixAlloy] Circuit breaker recovery — recreating engine");
        const freshEngine=mkEngine(cw||w2,ch||h2);
        eng.current=freshEngine;
        drawDefaultImage(freshEngine);
        return adapter.tick.bind(adapter);
      });

      console.log("[PixAlloy] Single-thread mode started");
    }
  },[]);

  const initDoneRef=useRef(false); // Prevents duplicate initEngine from effect re-runs
  useEffect(()=>{
    if(showSplash){initDoneRef.current=false;return;}
    if(!initDoneRef.current&&!bridgeRef.current&&!eng.current){
      initDoneRef.current=true;
      // initEngine is now async — fire and forget, overlay loop waits for bridge
      initEngine(splashW,splashH).then(()=>{clearToolState();requestAnimationFrame(()=>zoomToFit());}).catch(err=>console.error("[PixAlloy] Init failed:",err));
    }

    // Handle pending file open from splash screen
    if(pendingOpen){
      setPendingOpen(false);
      // Wait one frame for the file input to mount
      requestAnimationFrame(()=>{fi.current?.click();});
    }

    // ── Overlay-only rAF loop ──
    // Engine tick/render is owned by HostBridge. This loop handles:
    //   1. Overlay canvas (marching ants, cursor, guides, pen path, etc.)
    //   2. Dirty flag → React re-render
    //   3. Navigator + histogram (throttled)
    let lastHistUpdate=0;
    const overlayTick=()=>{
      const c=cvs.current,e=eng.current;
      const isWorker=workerModeRef.current;
      // In worker mode: we don't need eng.current — bridge handles everything
      // In single-thread mode: we need eng.current for direct state access
      if(!isWorker&&(!c||!e)){raf.current=requestAnimationFrame(overlayTick);return;}
      if(isWorker&&!bridgeRef.current){raf.current=requestAnimationFrame(overlayTick);return;}
      try{

      // ── Readback SAB: check dirty/selection/transform state ──
      // In single-thread mode, writeReadback() writes these after each tick.
      // In worker mode, the worker writes and we read via DataView.
      const rv=readbackViewRef.current;

      // Check dirty flag → trigger React re-render for UI panels
      if(rv){
        if(rv.getUint32(RB_DIRTY,true)){
          rv.setUint32(RB_DIRTY,0,true);
          // The init FILL_COLOR command sets dirty in the engine before the user
          // has interacted. Skip the first dirty readback so a brand-new document
          // isn't flagged as having unsaved changes.
          if(initCleanRef.current){initCleanRef.current=false;}
          else{dirtyRef.current=true;}
          fu();
        }
      }else if(e&&e.s.dirty){dirtyRef.current=true;e.s.dirty=false;fu();}

      // ── Overlay canvas ──
      const oc=ovlRef.current;
      const ox=oc?(ovlCtxRef.current||(ovlCtxRef.current=oc.getContext("2d"))):null;
      if(ox&&oc){
      ox.clearRect(0,0,oc.width,oc.height);
      const s=1/(panState.current.zoom||1); // screen-constant scale factor
      ao.current=(ao.current+0.6)%10;
      if(sd.current)antsPreview(ox,sd.current,ao.current,s);

      // Selection overlay
      // Single-thread: always prefer direct engine state — avoids one-frame gap
      // where readback hasn't been written yet but e.s.sel already exists.
      // Worker mode: read from the transferred selection mask cache.
      // BUG FIX: The wrapper object must use property name "mask" (not "data")
      // because ants() checks sel.mask on line 10 of overlays.js.
      if(!isWorker&&e&&e.s.sel){
        ants(ox,e.s.sel,ao.current,s);
      }else if(isWorker&&rv&&rv.getUint32(RB_HAS_SELECTION,true)){
        const sm=bridgeRef.current?.getSelectionMask();
        if(sm)ants(ox,{mask:sm.mask,x:sm.x,y:sm.y,w:sm.w,h:sm.h},ao.current,s);
      }

      // Transform overlay
      if(rv&&rv.getUint32(RB_HAS_TRANSFORM,true)){
        if(!isWorker&&e&&e.s.tfm){
          tfmH(ox,e.s.tfm,s);
        }else{
          const base=RB_TFM_DATA;
          tfmH(ox,{x:rv.getFloat32(base,true),y:rv.getFloat32(base+4,true),w:rv.getFloat32(base+8,true),h:rv.getFloat32(base+12,true),rot:rv.getFloat32(base+16,true),sx:rv.getFloat32(base+20,true),sy:rv.getFloat32(base+24,true),selOnly:rv.getUint32(base+28,true)===1},s);
        }
      }else if(!isWorker&&e&&e.s.tfm){
        tfmH(ox,e.s.tfm,s);
      }
      const dr=drag.current;
      if(dr?.type==="crop"&&dr.w>2&&dr.h>2){ox.save();ox.fillStyle="rgba(0,0,0,0.5)";
        ox.fillRect(0,0,oc.width,dr.y);
        ox.fillRect(0,dr.y,dr.x,dr.h);
        ox.fillRect(dr.x+dr.w,dr.y,oc.width-dr.x-dr.w,dr.h);
        ox.fillRect(0,dr.y+dr.h,oc.width,oc.height-dr.y-dr.h);
        ox.strokeStyle="#60a5fa";ox.lineWidth=1.5*s;ox.setLineDash([5*s,3*s]);ox.strokeRect(dr.x,dr.y,dr.w,dr.h);ox.setLineDash([]);
        ox.strokeStyle="rgba(255,255,255,0.2)";ox.lineWidth=0.5*s;
        for(let i=1;i<3;i++){ox.beginPath();ox.moveTo(dr.x+dr.w*i/3,dr.y);ox.lineTo(dr.x+dr.w*i/3,dr.y+dr.h);ox.stroke();ox.beginPath();ox.moveTo(dr.x,dr.y+dr.h*i/3);ox.lineTo(dr.x+dr.w,dr.y+dr.h*i/3);ox.stroke();}
        ox.font=(10*s)+"px monospace";ox.fillStyle="rgba(96,165,250,0.85)";ox.textAlign="center";
        ox.fillText(`${Math.round(dr.w)} \u00d7 ${Math.round(dr.h)}`,dr.x+dr.w/2,dr.y+dr.h+14*s);
        ox.restore();}
      if(dr?.type==="move"&&(Math.abs(dr.dx)>1||Math.abs(dr.dy)>1)){
        ox.save();
        // Outline showing where content will land
        ox.strokeStyle="#60a5fa";ox.lineWidth=1*s;ox.setLineDash([4*s,4*s]);
        ox.strokeRect(dr.dx,dr.dy,oc.width,oc.height);ox.setLineDash([]);
        // Origin crosshair
        ox.strokeStyle="rgba(96,165,250,0.4)";ox.lineWidth=0.5*s;
        ox.beginPath();ox.moveTo(dr.startX-8*s,dr.startY);ox.lineTo(dr.startX+8*s,dr.startY);
        ox.moveTo(dr.startX,dr.startY-8*s);ox.lineTo(dr.startX,dr.startY+8*s);ox.stroke();
        // Arrow from origin to current
        ox.strokeStyle="rgba(96,165,250,0.6)";ox.lineWidth=1*s;ox.setLineDash([3*s,3*s]);
        ox.beginPath();ox.moveTo(dr.startX,dr.startY);ox.lineTo(dr.startX+dr.dx,dr.startY+dr.dy);ox.stroke();ox.setLineDash([]);
        // Offset label
        ox.font=(10*s)+"px monospace";ox.fillStyle="rgba(96,165,250,0.9)";ox.textAlign="center";
        const lx=dr.startX+dr.dx/2,ly=dr.startY+dr.dy/2-8*s;
        ox.fillText(`\u0394${Math.round(dr.dx)}, ${Math.round(dr.dy)}`,lx,ly);
        ox.restore();
      }
      if(dr?.type==="gradient"){
        ox.save();
        const gx1=dr.startX,gy1=dr.startY,gx2=dr.endX,gy2=dr.endY;
        ox.strokeStyle="#fff";ox.lineWidth=1.5*s;ox.setLineDash([6*s,3*s]);
        ox.beginPath();ox.moveTo(gx1,gy1);ox.lineTo(gx2,gy2);ox.stroke();ox.setLineDash([]);
        ox.fillStyle="#fff";ox.strokeStyle="#000";ox.lineWidth=1*s;
        ox.beginPath();ox.arc(gx1,gy1,4*s,0,Math.PI*2);ox.fill();ox.stroke();
        ox.fillStyle="#000";ox.strokeStyle="#fff";
        ox.beginPath();ox.arc(gx2,gy2,4*s,0,Math.PI*2);ox.fill();ox.stroke();
        const dist=Math.round(Math.sqrt((gx2-gx1)**2+(gy2-gy1)**2));
        const angle=Math.round(Math.atan2(gy2-gy1,gx2-gx1)*180/Math.PI);
        ox.font=(10*s)+"px monospace";ox.fillStyle="rgba(96,165,250,0.9)";ox.textAlign="center";
        ox.fillText(`${dist}px  ${angle}\u00b0`,(gx1+gx2)/2,(gy1+gy2)/2-10*s);
        ox.restore();
      }
      // Brush cursor
      const b=bridgeRef.current;
      const cx2=b?b.getParam("cursorX"):(e?.s.p.cursorX||-99);
      const cy2=b?b.getParam("cursorY"):(e?.s.p.cursorY||-99);
      const bs=b?b.getParam("brushSize"):(e?.s.p.brushSize||12);
      const at=b?b.getParam("activeTool"):(e?.s.p.activeTool||0);
      if(cx2>0&&cy2>0&&(at===T.BRUSH||at===T.ERASER))bCur(ox,cx2,cy2,bs,s);

      // Pen hover overlay indicators
      if(at===T.PEN&&cx2>0&&cy2>0&&penHover){
        ox.save();
        if(penHover==="segment"){
          // + icon at cursor: indicates click will add anchor
          ox.strokeStyle="#4ade80";ox.lineWidth=1.5*s;ox.fillStyle="rgba(30,30,30,0.8)";
          ox.beginPath();ox.arc(cx2,cy2,7*s,0,Math.PI*2);ox.fill();ox.stroke();
          ox.beginPath();ox.moveTo(cx2-3.5*s,cy2);ox.lineTo(cx2+3.5*s,cy2);
          ox.moveTo(cx2,cy2-3.5*s);ox.lineTo(cx2,cy2+3.5*s);ox.stroke();
        } else if(penHover==="corner"){
          // Corner marker: indicates Alt+click will delete handles
          ox.strokeStyle="#f59e0b";ox.lineWidth=1.5*s;
          ox.beginPath();ox.rect(cx2-5*s,cy2-5*s,10*s,10*s);ox.stroke();
          ox.beginPath();ox.moveTo(cx2-3*s,cy2-3*s);ox.lineTo(cx2+3*s,cy2+3*s);
          ox.moveTo(cx2+3*s,cy2-3*s);ox.lineTo(cx2-3*s,cy2+3*s);ox.stroke();
        }
        ox.restore();
      }

      if(dr?.type==="penShape"){
        ox.save();ox.strokeStyle="rgba(96,165,250,0.5)";ox.lineWidth=1*s;ox.setLineDash([4*s,4*s]);
        const sx=Math.min(dr.startX,dr.endX),sy=Math.min(dr.startY,dr.endY);
        const sw2=Math.abs(dr.endX-dr.startX),sh2=Math.abs(dr.endY-dr.startY);
        ox.strokeRect(sx,sy,sw2,sh2);ox.setLineDash([]);
        ox.font=(10*s)+"px monospace";ox.fillStyle="rgba(96,165,250,0.8)";ox.textAlign="center";
        ox.fillText(`${Math.round(sw2)} \u00d7 ${Math.round(sh2)}`,sx+sw2/2,sy+sh2+14*s);
        ox.restore();
      }

      const penPaths=penPathRef.current;
      const apIdx=activePathIdxRef.current;
      if(penPaths.length>0&&at===T.PEN){
        ox.save();
        // Draw each path in the PathList
        for(let pi=0;pi<penPaths.length;pi++){
          const path=penPaths[pi];const pts=path.points;
          const isActive=pi===apIdx;
          if(pts.length>=2){
            ox.strokeStyle=isActive?"rgba(96,165,250,0.85)":"rgba(96,165,250,0.45)";
            ox.lineWidth=(isActive?1.5:1)*s;ox.setLineDash([]);
            ox.beginPath();
            drawPathCurve(ox,path);
            ox.stroke();
          }
          // Rubber-band line from last point to cursor (active open path only)
          if(isActive&&!path.closed&&pts.length>=1&&cx2>0&&cy2>0){
            const last=pts[pts.length-1];
            ox.strokeStyle="rgba(96,165,250,0.3)";ox.lineWidth=1*s;ox.setLineDash([4*s,4*s]);
            ox.beginPath();ox.moveTo(last.x,last.y);ox.lineTo(cx2,cy2);ox.stroke();ox.setLineDash([]);
          }
          // Draw anchor/handle points
          for(let i=0;i<pts.length;i++){
            const pt=pts[i];
            // Handle tangent lines
            if(isActive||pi===apIdx){
              ox.setLineDash([2*s,2*s]);ox.strokeStyle="rgba(96,165,250,0.5)";ox.lineWidth=1*s;
              if(pt.cp1x!=null){ox.beginPath();ox.moveTo(pt.x,pt.y);ox.lineTo(pt.cp1x,pt.cp1y);ox.stroke();
                ox.fillStyle="#1e1e1e";ox.strokeStyle="#60a5fa";ox.lineWidth=1*s;ox.setLineDash([]);
                ox.beginPath();ox.arc(pt.cp1x,pt.cp1y,3.5*s,0,Math.PI*2);ox.fill();ox.stroke();}
              if(pt.cp2x!=null){ox.setLineDash([2*s,2*s]);ox.strokeStyle="rgba(96,165,250,0.5)";ox.lineWidth=1*s;
                ox.beginPath();ox.moveTo(pt.x,pt.y);ox.lineTo(pt.cp2x,pt.cp2y);ox.stroke();
                ox.fillStyle="#1e1e1e";ox.strokeStyle="#60a5fa";ox.lineWidth=1*s;ox.setLineDash([]);
                ox.beginPath();ox.arc(pt.cp2x,pt.cp2y,3.5*s,0,Math.PI*2);ox.fill();ox.stroke();}
            }
            // Anchor square
            ox.setLineDash([]);
            ox.fillStyle=(isActive&&i===0)?"#2563eb":isActive?"#1e1e1e":"rgba(30,30,30,0.5)";
            ox.strokeStyle=isActive?"#60a5fa":"rgba(96,165,250,0.4)";ox.lineWidth=(isActive?1.5:1)*s;
            ox.beginPath();ox.rect(pt.x-4*s,pt.y-4*s,8*s,8*s);ox.fill();ox.stroke();
          }
          // Close proximity hint (active path only)
          if(isActive&&!path.closed&&pts.length>=3&&cx2>0&&cy2>0){
            const fp=pts[0];
            const dist2=Math.sqrt((cx2-fp.x)**2+(cy2-fp.y)**2);
            if(dist2<20){
              ox.strokeStyle="#4ade80";ox.lineWidth=2*s;ox.setLineDash([]);
              ox.beginPath();ox.arc(fp.x,fp.y,8*s,0,Math.PI*2);ox.stroke();
              ox.font=(9*s)+"px monospace";ox.fillStyle="#4ade80";ox.textAlign="center";
              ox.fillText("close",fp.x,fp.y-12*s);
            }
          }
          if(path.closed){
            ox.font=(9*s)+"px monospace";ox.fillStyle="rgba(74,222,128,0.7)";ox.textAlign="left";
            ox.fillText("\u2713 closed",pts[0].x+10*s,pts[0].y-8*s);
          }
        }
        ox.setLineDash([]);ox.restore();
      }

      // Bounding box + handles for active path (or all paths if only one)
      if(penPaths.length>0&&at===T.PEN){
        const drawBBox=(b2)=>{
          if(!b2||b2.w<1||b2.h<1)return;
          ox.save();
          ox.strokeStyle="rgba(96,165,250,0.7)";ox.lineWidth=1*s;ox.setLineDash([4*s,4*s]);
          ox.strokeRect(b2.x,b2.y,b2.w,b2.h);ox.setLineDash([]);
          const hs=5*s;
          for(const[hx2a,hy2]of[[b2.x,b2.y],[b2.x+b2.w,b2.y],[b2.x+b2.w,b2.y+b2.h],[b2.x,b2.y+b2.h]]){
            ox.fillStyle="#1e1e1e";ox.strokeStyle="#60a5fa";ox.lineWidth=1.5*s;
            ox.beginPath();ox.rect(hx2a-hs,hy2-hs,hs*2,hs*2);ox.fill();ox.stroke();
          }
          const rotX=b2.cx,rotY=b2.y-25*s;
          ox.strokeStyle="rgba(96,165,250,0.5)";ox.lineWidth=1*s;
          ox.beginPath();ox.moveTo(b2.cx,b2.y);ox.lineTo(rotX,rotY);ox.stroke();
          ox.fillStyle="#1e1e1e";ox.strokeStyle="#60a5fa";ox.lineWidth=1.5*s;
          ox.beginPath();ox.arc(rotX,rotY,5*s,0,Math.PI*2);ox.fill();ox.stroke();
          ox.font=(9*s)+"px monospace";ox.fillStyle="rgba(96,165,250,0.7)";ox.textAlign="center";
          ox.fillText(`${Math.round(b2.w)} \u00d7 ${Math.round(b2.h)}`,b2.cx,b2.y+b2.h+14*s);
          ox.restore();
        };
        // Show bbox for active path; if multiple paths, also show a dimmer total bbox
        const activePath=penPaths[apIdx];
        if(activePath&&activePath.points.length>=2)drawBBox(pathBounds(activePath));
        if(penPaths.length>1){
          const tb=pathListBounds(penPaths);
          if(tb&&tb.w>0&&tb.h>0){
            ox.save();ox.strokeStyle="rgba(96,165,250,0.25)";ox.lineWidth=1*s;ox.setLineDash([6*s,4*s]);
            ox.strokeRect(tb.x,tb.y,tb.w,tb.h);ox.setLineDash([]);ox.restore();
          }
        }
      }

      const curGuides=guidesRef.current;
      const activeGuideDrag=drag.current?.type==="guide"?drag.current:null;
      if(curGuides.length>0){
        ox.save();
        for(let gi=0;gi<curGuides.length;gi++){
          const g=curGuides[gi];
          const isActive=activeGuideDrag&&activeGuideDrag.idx===gi;
          ox.strokeStyle=isActive?"rgba(0,200,255,1)":"rgba(0,200,255,0.6)";
          ox.lineWidth=(isActive?2:1)*s;
          ox.setLineDash(isActive?[4*s,4*s]:[]);
          ox.beginPath();
          if(g.type==="h"){ox.moveTo(0,g.pos);ox.lineTo(oc.width,g.pos);}
          else{ox.moveTo(g.pos,0);ox.lineTo(g.pos,oc.height);}
          ox.stroke();
          if(isActive){
            ox.setLineDash([]);ox.font=(10*s)+"px monospace";ox.fillStyle="rgba(0,200,255,0.95)";
            if(g.type==="h"){ox.textAlign="left";ox.fillText(`${g.pos}px`,4,g.pos-5*s);}
            else{ox.textAlign="center";ox.fillText(`${g.pos}px`,g.pos,14*s);}
          }
        }
        ox.setLineDash([]);
        ox.restore();
      }

      const gd=guideDragRef.current;
      if(gd){
        ox.save();ox.strokeStyle="rgba(0,200,255,0.9)";ox.lineWidth=1*s;ox.setLineDash([4*s,4*s]);
        ox.beginPath();
        if(gd.type==="h"){ox.moveTo(0,gd.pos);ox.lineTo(oc.width,gd.pos);}
        else{ox.moveTo(gd.pos,0);ox.lineTo(gd.pos,oc.height);}
        ox.stroke();ox.setLineDash([]);
        ox.font=(10*s)+"px monospace";ox.fillStyle="rgba(0,200,255,0.9)";
        if(gd.type==="h"){ox.textAlign="left";ox.fillText(`${gd.pos}px`,4,gd.pos-4*s);}
        else{ox.textAlign="center";ox.fillText(`${gd.pos}px`,gd.pos,12*s);}
        ox.restore();
      }
      } // end overlay canvas block

      // Navigator + histogram (throttled — skip during active strokes for perf)
      const now=performance.now();
      const strokeOn=rv?rv.getUint32(RB_STROKE_ON,true):(e?e.s.strokeOn:false);
      if(now-lastHistUpdate>2000&&!strokeOn){lastHistUpdate=now;if(c&&!isWorker)updateNav(c);if(e)updateHist(e);}
      else if(now-lastHistUpdate>500&&c&&!isWorker){updateNav(c);} // nav is cheap, update more often
      }catch(err){console.error("PixAlloy overlay tick error:",err);}
      raf.current=requestAnimationFrame(overlayTick);
    };
    raf.current=requestAnimationFrame(overlayTick);
    return()=>{
      if(raf.current)cancelAnimationFrame(raf.current);
      // Don't destroy bridge here — it persists across effect re-runs
    };
  },[showSplash,splashW,splashH,initEngine,pendingOpen]);

  // Cleanup: destroy bridge on component unmount
  useEffect(()=>{
    return()=>{
      if(eng.current){eng.current.destroy();eng.current=null;}
      if(bridgeRef.current){bridgeRef.current.destroy();bridgeRef.current=null;}
      readbackViewRef.current=null;layerSyncRef.current=null;
      workerModeRef.current=false;selMaskRef.current=null;tfmBitmapRef.current=null;
    };
  },[]);

  // ── STABILITY: Worker health monitoring ──
  // If we're in worker mode and the dirty flag hasn't changed for 60 seconds
  // while the tab is visible, the worker may have silently died.
  const lastDirtyTime=useRef(Date.now());
  const workerStaleNotified=useRef(false);
  useEffect(()=>{
    if(showSplash)return;
    const healthCheck=setInterval(()=>{
      if(!workerModeRef.current||document.hidden||showSplash)return;
      const rv=readbackViewRef.current;
      if(rv){
        const dirty=rv.getUint32(0,true);
        if(dirty){lastDirtyTime.current=Date.now();workerStaleNotified.current=false;}
        else if(Date.now()-lastDirtyTime.current>60000&&!workerStaleNotified.current){
          workerStaleNotified.current=true;
          console.warn("[PixAlloy] Worker appears stale — no activity for 60s");
          setToast({id:Date.now(),msg:"Worker appears unresponsive. Save your work. (File → Export PNG)",type:"error"});
        }
      }
    },10000);
    return()=>clearInterval(healthCheck);
  },[showSplash]);

  // ── STABILITY: Tab visibility change ──
  // When the tab is hidden, browsers throttle rAF to 0fps and may kill workers.
  // When restored, we need to verify the engine/bridge is still alive.
  useEffect(()=>{
    const onVisChange=()=>{
      if(!document.hidden&&!showSplash){
        // Tab became visible again — check if bridge/engine is still alive
        lastDirtyTime.current=Date.now(); // reset stale timer
        workerStaleNotified.current=false;
        if(workerModeRef.current&&bridgeRef.current){
          // Poke the bridge to see if it's still responsive
          try{bridgeRef.current.getParam("cursorX");}
          catch(err){
            console.error("[PixAlloy] Bridge unresponsive after tab restore:",err);
            setToast({id:Date.now(),msg:"Connection to worker lost. Save your work and reload.",type:"error"});
          }
        }
      }
    };
    document.addEventListener("visibilitychange",onVisChange);
    return()=>document.removeEventListener("visibilitychange",onVisChange);
  },[showSplash]);

  // ── STABILITY: Global error handler ──
  // Catch unhandled errors/rejections and show toast instead of silent failure
  useEffect(()=>{
    const onErr=(e)=>{
      if(showSplash)return; // ignore errors during splash
      console.error("[PixAlloy] Unhandled error:",e.error||e.reason||e.message);
      setToast({id:Date.now(),msg:"Unexpected error: "+(e.error?.message||e.reason?.message||e.message||"unknown")+". Save your work.",type:"error"});
    };
    window.addEventListener("error",onErr);
    window.addEventListener("unhandledrejection",onErr);
    return()=>{window.removeEventListener("error",onErr);window.removeEventListener("unhandledrejection",onErr);};
  },[showSplash]);

  // ── STABILITY: Warn before closing tab with unsaved work ──
  useEffect(()=>{
    const onBeforeUnload=(e)=>{
      if(!showSplash&&(dirtyRef.current||eng.current||bridgeRef.current)){
        e.preventDefault();
        e.returnValue="You have unsaved work. Are you sure you want to leave?";
      }
    };
    window.addEventListener("beforeunload",onBeforeUnload);
    return()=>window.removeEventListener("beforeunload",onBeforeUnload);
  },[showSplash]);

  function updateNav(mainC){
    const nc=navCvs.current;if(!nc||!mainC)return;
    const ctx=nc.getContext("2d",{willReadFrequently:true});
    ctx.clearRect(0,0,nc.width,nc.height);
    ctx.fillStyle="#111";ctx.fillRect(0,0,nc.width,nc.height);
    const scale=Math.min(nc.width/mainC.width,nc.height/mainC.height);
    const dw=mainC.width*scale,dh=mainC.height*scale;
    const dx=(nc.width-dw)/2,dy=(nc.height-dh)/2;
    ctx.drawImage(mainC,dx,dy,dw,dh);
  }

  function updateHist(e){
    const hc=histCvs.current;if(!hc||!e)return;
    const ctx=hc.getContext("2d",{willReadFrequently:true});
    const w=hc.width,h=hc.height;
    ctx.clearRect(0,0,w,h);ctx.fillStyle="#1e1e1e";ctx.fillRect(0,0,w,h);
    const hist=e.getHistogram();if(!hist)return;
    const drawCh=(bins,color)=>{const maxV=Math.max(...bins,1);ctx.beginPath();ctx.moveTo(0,h);for(let x=0;x<w;x++){const bi=Math.min(255,Math.round(x*256/w));const v=h-(bins[bi]/maxV)*h*0.92;ctx.lineTo(x,v);}ctx.lineTo(w,h);ctx.closePath();ctx.fillStyle=color;ctx.fill();};
    if(histChRef.current==="rgb"||histChRef.current==="r")drawCh(hist.r,"rgba(248,113,113,0.55)");
    if(histChRef.current==="rgb"||histChRef.current==="g")drawCh(hist.g,"rgba(74,222,128,0.45)");
    if(histChRef.current==="rgb"||histChRef.current==="b")drawCh(hist.b,"rgba(96,165,250,0.5)");
    if(histChRef.current==="lum")drawCh(hist.lum,"rgba(200,200,200,0.5)");
    ctx.strokeStyle="#2e2e2e";ctx.lineWidth=1;[0.25,0.5,0.75].forEach(xp=>{ctx.beginPath();ctx.moveTo(xp*w,0);ctx.lineTo(xp*w,h);ctx.stroke();});
    if(hist.r[0]+hist.g[0]+hist.b[0]>500){ctx.fillStyle="rgba(96,165,250,0.8)";ctx.fillRect(0,0,3,h);}
    if(hist.r[255]+hist.g[255]+hist.b[255]>500){ctx.fillStyle="rgba(96,165,250,0.8)";ctx.fillRect(w-3,0,3,h);}
    const wc=waveCvs.current;if(wc&&showWaveRef.current){const wx=wc.getContext("2d",{willReadFrequently:true});wx.clearRect(0,0,wc.width,wc.height);wx.fillStyle="#1a1a1a";wx.fillRect(0,0,wc.width,wc.height);try{const comp=new OffscreenCanvas(e.s.w,e.s.h);const cc=comp.getContext("2d",{willReadFrequently:true});for(const l of e.s.layers){if(!l.vis)continue;cc.globalAlpha=l.op;cc.drawImage(l.c,0,0);}const id=cc.getImageData(0,0,e.s.w,e.s.h).data;wx.globalAlpha=0.04;const step=Math.max(1,Math.floor(e.s.w/wc.width));for(let x=0;x<e.s.w;x+=step){const sx=Math.round(x*wc.width/e.s.w);for(let y2=0;y2<e.s.h;y2+=4){const i2=(y2*e.s.w+x)*4;const lv=id[i2]*0.299+id[i2+1]*0.587+id[i2+2]*0.114;wx.fillStyle="#fff";wx.fillRect(sx,wc.height-Math.round(lv/255*wc.height),1,1);}}wx.globalAlpha=1;}catch(err){}}
  }

  function drawDefaultImage(e){
    const L=e.s.layers[0],w=e.s.w,h=e.s.h,ctx=L.x;
    ctx.fillStyle="#ffffff";ctx.fillRect(0,0,w,h);
  }

  // ── Bridge-backed param writer + command pusher ──
  // wp(name, value) → bridge.setParam (writes to SharedArrayBuffer via Atomics)
  // pc(type, payload) → tiered routing:
  //   1. No-payload commands → bridge.pushSimple (ring buffer, zero postMessage overhead)
  //   2. Simple numeric payloads → bridge.pushCommand (ring buffer with DataView writer)
  //   3. Complex payloads (strings, arrays, objects) → bridge.pushSideband (MessageChannel in worker, direct in single-thread)
  const wp=useCallback((n,v)=>bridgeRef.current?.setParam(n,v),[]);

  // Commands that carry no payload — routed via ring buffer pushSimple
  const SIMPLE_CMDS=useRef(new Set([C.UNDO,C.REDO,C.LAYER_ADD,C.LAYER_DEL,C.STROKE_ON,C.STROKE_OFF,C.FLATTEN,C.LAYER_CLR,C.LAYER_UP,C.LAYER_DN,C.SEL_CLR,C.SEL_ALL,C.SEL_INV,C.TFM_BEGIN,C.TFM_APPLY,C.TFM_CANCEL,C.TFM_SEL_BEGIN,C.TFM_SEL_APPLY,C.COPY,C.CUT,C.MERGE_DOWN,C.MASK_ADD,C.MASK_DEL,C.IMG_FLIP_H,C.IMG_FLIP_V,C.IMG_ROT90,C.IMG_ROT180,C.GROUP_ADD,C.CONTENT_FILL,C.GROUP_MOVE_IN,C.GROUP_MOVE_OUT,C.ZOOM_FIT,C.LAYER_FLIP_H,C.LAYER_FLIP_V]));

  const pc=useCallback((t,pl)=>{
    // Record command for action playback (Phase 9) — uses ref to avoid stale closure
    if(actionsHook.recordingRef.current)actionsHook.recordCmd(t,pl);
    const b=bridgeRef.current;
    const sb=sidebandRef.current;
    // No payload → try ring buffer for zero-overhead delivery
    if(!pl||Object.keys(pl).length===0){
      if(b&&SIMPLE_CMDS.current.has(t)){b.pushSimple(t);return;}
      // Fallback: sideband (works in both worker and single-thread modes)
      if(b){b.pushSideband(t,{});return;}
      sb?.pushRaw(t,{});return;
    }
    // Simple numeric payloads that fit in 28-byte ring slots
    if(b){
      if(t===C.FILTER&&pl.id!=null){b.pushU32(t,pl.id);return;}
      if((t===C.SEL_RECT||t===C.SEL_ELLIPSE||t===C.CROP)&&pl.x!=null&&!pl.add&&!pl.sub){
        b.pushCommand(t,0,(v,off)=>{v.setFloat32(off,pl.x,true);v.setFloat32(off+4,pl.y,true);v.setFloat32(off+8,pl.w,true);v.setFloat32(off+12,pl.h,true);});return;
      }
      if(t===C.MOVE&&pl.dx!=null){b.pushF32Pair(t,pl.dx,pl.dy);return;}
      if(t===C.SEL_FEATHER&&pl.radius!=null){b.pushCommand(t,0,(v,off)=>{v.setFloat32(off,pl.radius,true);});return;}
      if(t===C.GAUSS_BLUR&&pl.radius!=null){b.pushCommand(t,0,(v,off)=>{v.setFloat32(off,pl.radius,true);});return;}
      if((t===C.IMG_RESIZE||t===C.CANVAS_SIZE)&&pl.w!=null){b.pushCommand(t,0,(v,off)=>{v.setUint32(off,pl.w,true);v.setUint32(off+4,pl.h,true);});return;}
      if(t===C.LAYER_REORDER&&pl.from!=null){b.pushCommand(t,0,(v,off)=>{v.setUint32(off,pl.from,true);v.setUint32(off+4,pl.to,true);});return;}
      if(t===C.HIST_JUMP&&pl.idx!=null){b.pushU32(t,pl.idx);return;}
      if(t===C.RUBBER_STAMP_SET&&pl.x!=null){b.pushF32Pair(t,pl.x,pl.y);return;}
      if(t===C.FILL_COLOR&&pl.r!=null&&!pl.label){b.pushCommand(t,0,(v,off)=>{v.setUint32(off,pl.r,true);v.setUint32(off+4,pl.g,true);v.setUint32(off+8,pl.b,true);});return;}
      if(t===C.SEL_FROM_ALPHA&&pl.idx!=null){b.pushCommand(t,0,(v,off)=>{v.setUint32(off,pl.idx,true);v.setUint32(off+4,pl.mode==='add'?1:0,true);});return;}
    }
    // Complex payloads → sideband (MessageChannel in worker, direct call in single-thread)
    if(b){b.pushSideband(t,pl||{});return;}
    sb?.pushRaw(t,pl||{});
  },[]);
  const setTool=useCallback(id=>{
    // Auto-apply pending transform when switching away from transform tool
    if(toolR.current===T.TRANSFORM&&id!==T.TRANSFORM){
      const tf=eng.current?.s.tfm;
      if(tf){
        if(tf.selOnly){pc(C.TFM_SEL_APPLY);}
        else{pc(C.TFM_APPLY);}
        fu();
      }
    }
    _setTool(id);toolR.current=id;wp("activeTool",id);pc(C.STROKE_OFF);wp("cursorDown",0);
  },[wp,pc,fu]);

  // ── Helpers for reading state in both worker and single-thread modes ──
  // In worker mode, readback SAB provides flags; in single-thread mode, read engine directly.
  const rbHasSel=useCallback(()=>{
    const rv=readbackViewRef.current;
    if(rv)return!!rv.getUint32(RB_HAS_SELECTION,true);
    return!!eng.current?.s.sel;
  },[]);
  const rbHasTfm=useCallback(()=>{
    const rv=readbackViewRef.current;
    if(rv)return!!rv.getUint32(RB_HAS_TRANSFORM,true);
    return!!eng.current?.s.tfm;
  },[]);
  const rbActIdx=useCallback(()=>{
    const rv=readbackViewRef.current;
    if(rv)return rv.getUint32(RB_ACTIVE_LAYER,true);
    return eng.current?.s.act??0;
  },[]);

  // Brush param sync handled by useBrushState hook
  useEffect(()=>{bgColRef.current=bgCol;},[bgCol]);
  useEffect(()=>{wp("brightness",bright/100);wp("contrast",contrast/100);wp("saturation",sat/100);},[bright,contrast,sat,wp]);
  useEffect(()=>{if(eng.current)eng.current.s.clipOverlay=showClip;/* In worker mode, clip overlay is handled by the worker */},[showClip]);

  // Attach wheel handler with passive:false to allow preventDefault
  useEffect(()=>{
    const el=vpContainerRef.current;if(!el)return;
    const handler=e=>{e.preventDefault();const ps=panState.current;const factor=e.deltaY<0?1.1:1/1.1;ps.zoom=Math.max(0.1,Math.min(8,ps.zoom*factor));applyVPTransform();};
    el.addEventListener("wheel",handler,{passive:false});
    return()=>el.removeEventListener("wheel",handler);
  },[applyVPTransform]);
  useEffect(()=>{showWaveRef.current=showWave;},[showWave]);
  useEffect(()=>{histChRef.current=histCh;},[histCh]);

  const SNAP_DIST=6; // pixels in canvas space to snap to a guide

  // Snap a value to the nearest guide (returns snapped value or original)
  const snapToGuides=useCallback((val,type)=>{
    if(!snapEnabled)return val;
    const gs=guidesRef.current;
    for(const g of gs){
      if(g.type===type&&Math.abs(g.pos-val)<=SNAP_DIST)return g.pos;
    }
    // Also snap to canvas edges and center
    const dim=type==="h"?ch:cw;
    if(Math.abs(val)<=SNAP_DIST)return 0;
    if(Math.abs(val-dim)<=SNAP_DIST)return dim;
    if(Math.abs(val-dim/2)<=SNAP_DIST)return dim/2;
    return val;
  },[cw,ch]);

  // Snap a rect {x,y,w,h} edges to guides, returns adjusted rect
  const snapRect=useCallback((rx,ry,rw,rh)=>{
    if(!snapEnabled)return{x:rx,y:ry,w:rw,h:rh};
    const gs=guidesRef.current;
    let sx=rx,sy=ry,sw=rw,sh=rh;
    // Snap left edge
    for(const g of gs){if(g.type==="v"&&Math.abs(rx-g.pos)<=SNAP_DIST){sx=g.pos;break;}}
    // Snap right edge
    for(const g of gs){if(g.type==="v"&&Math.abs(rx+rw-g.pos)<=SNAP_DIST){sw=g.pos-sx;break;}}
    // Snap top edge
    for(const g of gs){if(g.type==="h"&&Math.abs(ry-g.pos)<=SNAP_DIST){sy=g.pos;break;}}
    // Snap bottom edge
    for(const g of gs){if(g.type==="h"&&Math.abs(ry+rh-g.pos)<=SNAP_DIST){sh=g.pos-sy;break;}}
    return{x:sx,y:sy,w:sw,h:sh};
  },[]);

  // Clear all transient tool state (pen path, selection drag, lasso, etc.)
  const clearToolState=useCallback(()=>{
    setPenPath([]);penDragRef.current=null;setPenPreset("none");setActivePathIdx(0);
    sd.current=null;ss.current=null;polyRef.current=[];lassoRef.current=[];drag.current=null;
  },[]);

  // Pointer handlers — extracted to hooks/usePointer.js
  const { gp, onDown: onDownBase, onMove, onUp, onLeave, onDblClick } = usePointer({
    cvs, eng, panState, vpRef,
    toolR, rKeyHeldRef, spaceHeldRef, workerModeRef,
    ss, sd, drag, lassoRef, polyRef,
    bcolRef, fillModeRef, fillCol2Ref, gradModeRef, gradStopsRef,
    eyedropSampleRef, wandTolRef, penPathRef, penDragRef, penPresetRef, penUndoRef, penRedoRef,
    readbackViewRef, guidesRef, bridgeRef, textSeedRef, activePathIdxRef,
    wp, pc, fu,
    applyVPTransform, setRotDisplay,
    setCurPos, setCurRGB, setBcol, setGuides, setNearGuide, setTfmCursor,
    setPenPath, setPenPathWithUndo, setPenHover, setShowText, setSelCtxMenu, setActivePathIdx,
    rbHasSel, rbHasTfm, rbActIdx,
    snapToGuides, snapRect, setTool,
  });
  // Color Range mode intercept — click samples a pixel and creates selection
  const onDown=useCallback(e=>{
    if(colorRangeMode){
      const pt=gp(e);
      pc(C.SEL_COLOR_RANGE,{x:pt.x,y:pt.y,fuzziness:colorRangeFuzz,add:e.shiftKey,sub:e.altKey});
      fu();setColorRangeMode(false);
      return;
    }
    onDownBase(e);
  },[colorRangeMode,colorRangeFuzz,gp,pc,fu,onDownBase]);

  // Responsive layout (Phase 2.1)
  const responsive = useResponsive();
  const { isTablet } = responsive;

  // Touch gestures — attaches touch listeners for pinch/pan (Phase 2.3)
  // Single-finger events go directly to usePointer handlers — no wrapping
  useTouchGestures({
    panState, applyVPTransform, isTablet,
    vpContainerRef,
    pc, C, fu, zoomToFit,
  });

  // Wheel zoom handled via useEffect with passive:false (see above)

  // Load image as bitmap helper
  const loadBitmap=useCallback(async(f)=>{
    try{
      const bmp=await createImageBitmap(f);
      // For very large images, decode a downsampled version first for instant preview
      // then return the full bitmap for actual document creation
      const MAX_DECODE=8192; // beyond this, browsers stall on drawImage
      if(bmp.width>MAX_DECODE||bmp.height>MAX_DECODE){
        const scale=Math.min(MAX_DECODE/bmp.width,MAX_DECODE/bmp.height);
        const rw=Math.round(bmp.width*scale),rh=Math.round(bmp.height*scale);
        try{
          const resized=await createImageBitmap(f,{resizeWidth:rw,resizeHeight:rh,resizeQuality:"high"});
          bmp.close(); // free the oversized bitmap
          return resized;
        }catch{return bmp;} // fallback if resize not supported
      }
      return bmp;
    }
    catch{return new Promise((res,rej)=>{const rd=new FileReader();rd.onload=()=>{const img=new Image();img.onload=()=>res(img);img.onerror=rej;img.src=rd.result;};rd.readAsDataURL(f);});}
  },[]);

  // Core open-as-document logic — extracted so save guard can call it too
  // In worker mode: destroy the worker, reinit in single-thread mode (need engine access to load pixels)
  const doOpenBmp=useCallback(async(bmp)=>{
    // If we're in worker mode or have no engine, destroy and reinit in single-thread
    if(workerModeRef.current||!eng.current){
      if(eng.current){eng.current.destroy();}
      if(bridgeRef.current){bridgeRef.current.destroy();bridgeRef.current=null;}
      eng.current=null;sidebandRef.current=null;readbackViewRef.current=null;layerSyncRef.current=null;
      workerModeRef.current=false;selMaskRef.current=null;tfmBitmapRef.current=null;cvsCtxRef.current=null;ovlCtxRef.current=null;
      // Null out the canvas ref so we can detect when React provides a fresh one
      const oldCanvas=cvs.current;
      cvs.current=null;
      // Bump session to force React to remount fresh canvas elements
      setSessionId(s=>s+1);
      // Wait until React re-renders and cvsRefCb fires with a new canvas element
      await new Promise(resolve=>{
        const check=()=>{
          if(cvs.current&&cvs.current!==oldCanvas){resolve();return;}
          requestAnimationFrame(check);
        };
        requestAnimationFrame(check);
      });
      await initEngine(bmp.width,bmp.height,"single");
    }
    const E2=eng.current;
    if(!E2){console.error("[PixAlloy] doOpenBmp: engine not available after init");return;}
    const nw=bmp.width,nh=bmp.height;
    E2.s.w=nw;E2.s.h=nh;
    E2.s.layers=[mkL("Background",nw,nh)];E2.s.act=0;
    E2.s.undo=[];E2.s.redo=[];E2.s.sel=null;E2.s.tfm=null;E2.s.history=[{icon:"🖼",label:"Open Image"}];
    const L=E2.s.layers[0];
    try{L.x.drawImage(bmp,0,0);}catch(e2){
      try{const tmp=document.createElement("canvas");tmp.width=nw;tmp.height=nh;tmp.getContext("2d",{willReadFrequently:true}).drawImage(bmp,0,0);L.x.putImageData(tmp.getContext("2d",{willReadFrequently:true}).getImageData(0,0,nw,nh),0,0);}
      catch(e3){L.x.fillStyle="#fff";L.x.fillRect(0,0,nw,nh);}
    }
    if(cvs.current){bridgeRef.current?.resize(nw,nh,true);}if(ovlRef.current){ovlRef.current.width=nw;ovlRef.current.height=nh;ovlCtxRef.current=null;}
    setCw(nw);setCh(nh);
    if(E2.s.onDimsChanged)E2.s.onDimsChanged(nw,nh);
    if(bmp.close)bmp.close();dirtyRef.current=false;
    clearToolState();
    // Zoom to fit the opened image in the viewport
    requestAnimationFrame(()=>zoomToFit());
    fu();
  },[initEngine,fu]);

  // File open — supports 'open' (replace) and 'layer' (add as layer) modes
  // PSD import — parses layers, opens via doOpenBmp, then swaps in PSD layers
  const doOpenPSD=useCallback(async(file)=>{
    const result=await importPSD(file);
    if(result.warnings.length>0)console.warn("[PixAlloy] PSD import warnings:",result.warnings);

    // Composite all visible layers into a single bitmap
    const comp=new OffscreenCanvas(result.width,result.height);
    const cx=comp.getContext("2d");
    for(const l of result.layers){
      if(!l.vis||l.type==="group")continue;
      cx.globalAlpha=l.op;
      cx.globalCompositeOperation=l.blend||"source-over";
      cx.drawImage(l.c,0,0);
      cx.globalAlpha=1;cx.globalCompositeOperation="source-over";
    }
    const bmp=comp.transferToImageBitmap();

    // Open via the proven doOpenBmp path — handles worker→single lifecycle
    await doOpenBmp(bmp);

    // Now replace the single Background layer with the parsed PSD layers.
    const E2=eng.current;
    if(E2){
      // Destroy GL compositor so it reinits with fresh textures for the new layers
      if(E2.s._gl){E2.s._gl.destroy();E2.s._gl=null;}
      E2.s._glCanvas=null;
      E2.s._glTried=false;

      // Replace layers
      E2.s.layers=result.layers;
      E2.s.act=result.layers.length-1;
      E2.s.history=[{icon:"📄",label:`Open PSD (${result.layerCount} layers)`}];
      E2.s.sel=null;E2.s.tfm=null;
      E2.s.undo=[];E2.s.redo=[];
      E2.s.dirty=true;
      for(const l of E2.s.layers){l._glDirty=true;l._undoClean=false;}
    }

    if(toastTimer.current)clearTimeout(toastTimer.current);
    const warnCount=result.warnings.length;
    setToast({msg:`PSD imported: ${result.layerCount} layers, ${result.width}×${result.height}${warnCount>0?` (${warnCount} warnings)`:''}`,type:"info",id:Date.now()});
    toastTimer.current=setTimeout(()=>setToast(null),4000);
    fu();
  },[doOpenBmp,fu]);

  const onFile=useCallback(async(e)=>{const f=e.target.files?.[0];if(!f)return;
    const mode=fiModeRef.current;
    const ext=f.name.toLowerCase().split('.').pop();

    // PSD files get their own import path
    if(ext==='psd'){
      if(dirtyRef.current){
        // Can't pass a File to save guard (it expects bmp), so read it now
        setSaveGuard({action:"open",psdFile:f});
      } else {
        await doOpenPSD(f);
      }
      fiModeRef.current="open";e.target.value="";return;
    }

    // SVG/EPS/AI vector file import → vector layers
    if(ext==='svg'||ext==='eps'||ext==='ai'){
      try{
        const text=await f.text();
        const result=parseVectorFile(text,f.name);
        if(result&&result.layers.length>0){
          const E2=eng.current;
          if(!E2){console.warn("[PixAlloy] Vector import requires single-thread mode");fiModeRef.current="open";e.target.value="";return;}
          const vw=result.viewBox?.w||result.bbox?.w||E2.s.w;
          const vh=result.viewBox?.h||result.bbox?.h||E2.s.h;
          const scale=Math.min(E2.s.w/vw,E2.s.h/vh,2);
          const offsetX=(E2.s.w-vw*scale)/2;
          const offsetY=(E2.s.h-vh*scale)/2;
          for(const vl of result.layers){
            const scaledPaths=vl.paths.map(pa=>({
              ...pa,
              points:pa.points.map(pt=>{
                const r={...pt,x:pt.x*scale+offsetX,y:pt.y*scale+offsetY};
                if(pt.cp1x!=null){r.cp1x=pt.cp1x*scale+offsetX;r.cp1y=pt.cp1y*scale+offsetY;}
                if(pt.cp2x!=null){r.cp2x=pt.cp2x*scale+offsetX;r.cp2y=pt.cp2y*scale+offsetY;}
                return r;
              }),
            }));
            pc(C.VECTOR_LAYER_ADD,{paths:scaledPaths,style:vl.style||{},name:vl.name||f.name.replace(/\.[^.]+$/,"")});
          }
          fu();
          setToast({id:Date.now(),msg:`Imported ${result.layers.length} vector layer${result.layers.length!==1?"s":""} from ${f.name}`,type:"info"});
        } else {
          setToast({id:Date.now(),msg:`No vector shapes found in ${f.name}`,type:"error"});
        }
      }catch(err){console.error("[PixAlloy] Vector import error:",err);setToast({id:Date.now(),msg:"Vector import failed: "+err.message,type:"error"});}
      fiModeRef.current="open";e.target.value="";return;
    }

    const bmp=await loadBitmap(f);
    if(mode==="layer"){
      const E2=eng.current;
      if(!E2){
        // Worker mode: "Open as Layer" not yet supported, fall back to open-as-document
        console.warn("[PixAlloy] Open as Layer not supported in worker mode, opening as document instead");
        if(dirtyRef.current){setSaveGuard({action:"open",bmp});}else{await doOpenBmp(bmp);}
        fiModeRef.current="open";e.target.value="";return;
      }
      E2.pushCommand({type:C.LAYER_ADD,payload:{}});
      const L=E2.s.layers[E2.s.act];if(L){
        const scale=Math.min(E2.s.w/bmp.width,E2.s.h/bmp.height,1);
        const dw=Math.round(bmp.width*scale),dh=Math.round(bmp.height*scale);
        const dx=Math.round((E2.s.w-dw)/2),dy=Math.round((E2.s.h-dh)/2);
        L.x.drawImage(bmp,dx,dy,dw,dh);L.name=f.name.replace(/\.[^.]+$/,"");
      }
      if(bmp.close)bmp.close();fu();
    } else {
      if(dirtyRef.current){
        setSaveGuard({action:"open",bmp});
      } else {
        await doOpenBmp(bmp);
      }
    }
    fiModeRef.current="open";e.target.value="";
  },[pc,fu,loadBitmap,doOpenBmp,doOpenPSD]);

  const doExport=useCallback(async(fmt)=>{
    const b=bridgeRef.current;
    const e=eng.current;

    // Helper: given a bitmap/canvas, download as blob
    const downloadBitmap=(source,w2,h2,mime,qual,fname)=>{
      const rc=document.createElement("canvas");rc.width=w2;rc.height=h2;
      const rx=rc.getContext("2d",{willReadFrequently:true});
      rx.drawImage(source,0,0,w2,h2);
      try{
        rc.toBlob(bl=>{
          if(!bl)return;
          const url=URL.createObjectURL(bl);
          const a=document.createElement("a");a.href=url;a.download=fname||`pixalloy.${fmt.e}`;
          document.body.appendChild(a);a.click();document.body.removeChild(a);
          setTimeout(()=>URL.revokeObjectURL(url),1000);
        },mime||fmt.m,qual||0.92);
      }catch(err){
        try{
          const dataUrl=rc.toDataURL(mime||fmt.m,qual||0.92);
          const a=document.createElement("a");a.href=dataUrl;a.download=fname||`pixalloy.${fmt.e}`;
          document.body.appendChild(a);a.click();document.body.removeChild(a);
        }catch(e2){console.error("Export failed:",e2);}
      }
    };

    if(workerModeRef.current&&b){
      try{
        const bitmap=await b.requestExport();
        downloadBitmap(bitmap,bitmap.width,bitmap.height);
        bitmap.close();
      }catch(err){console.error("Worker export failed:",err);}
    }else if(e){
      const offC=e.composite();
      downloadBitmap(offC,e.s.w,e.s.h);
    }
  },[]);

  // Enhanced export with scale, quality, filename (Phase 9)
  const doExportAdv=useCallback(async(opts)=>{
    const e=eng.current;const b=bridgeRef.current;
    const{mime,quality,scale,filename}=opts;
    const getComposite=async()=>{
      if(workerModeRef.current&&b){try{return await b.requestExport();}catch{return null;}}
      if(e)return e.composite();
      return null;
    };
    const source=await getComposite();if(!source)return;
    const sw=Math.round((source.width||e?.s.w||800)*scale);
    const sh=Math.round((source.height||e?.s.h||600)*scale);
    const rc=document.createElement("canvas");rc.width=sw;rc.height=sh;
    const rx=rc.getContext("2d");rx.drawImage(source,0,0,sw,sh);
    if(source.close)source.close();
    try{
      rc.toBlob(bl=>{
        if(!bl)return;
        const url=URL.createObjectURL(bl);
        const a=document.createElement("a");a.href=url;a.download=filename;
        document.body.appendChild(a);a.click();document.body.removeChild(a);
        setTimeout(()=>URL.revokeObjectURL(url),1000);
      },mime,quality);
    }catch(err){console.error("Export failed:",err);}
  },[]);

  // PSD export — saves current document as a PSD file with all layers
  const doExportPSD=useCallback(()=>{
    const E2=eng.current;
    if(!E2)return;
    try{
      const blob=exportPSD(E2.s.layers,E2.s.w,E2.s.h);
      const url=URL.createObjectURL(blob);
      const a=document.createElement("a");a.href=url;a.download="pixalloy.psd";
      document.body.appendChild(a);a.click();document.body.removeChild(a);
      setTimeout(()=>URL.revokeObjectURL(url),1000);
      dirtyRef.current=false;
    }catch(err){
      console.error("[PixAlloy] PSD export failed:",err);
      if(toastTimer.current)clearTimeout(toastTimer.current);
      setToast({msg:`PSD export failed: ${err.message}`,type:"error",id:Date.now()});
      toastTimer.current=setTimeout(()=>setToast(null),5000);
    }
  },[]);

  // Flatten image — composites all visible layers into a single Background layer
  const doFlatten=useCallback(()=>{
    const E2=eng.current;
    if(!E2||E2.s.layers.length<=1)return;
    // Composite all visible layers
    const comp=new OffscreenCanvas(E2.s.w,E2.s.h);
    const cx=comp.getContext("2d");
    for(const l of E2.s.layers){
      if(!l.vis||l.type==="group")continue;
      cx.globalAlpha=l.op??1;
      cx.globalCompositeOperation=l.blend||"source-over";
      cx.drawImage(l.c,0,0);
      cx.globalAlpha=1;cx.globalCompositeOperation="source-over";
    }
    // Replace all layers with single Background
    const bg=mkL("Background",E2.s.w,E2.s.h,false);
    bg.x.drawImage(comp,0,0);
    // Reset GL compositor to avoid stale texture cache
    if(E2.s._gl){E2.s._gl.destroy();E2.s._gl=null;}
    E2.s._glTried=false;
    E2.s.layers=[bg];
    E2.s.act=0;
    for(const l of E2.s.layers){l._glDirty=true;l._undoClean=false;}
    E2.s.history.push({icon:"🔲",label:"Flatten Image"});
    fu();
  },[fu]);

  // Shared "go back to splash" teardown — called from keyboard shortcut, menu, and save guard
  const resetToSplash=useCallback(()=>{
    // Log the caller for debugging silent splash returns
    console.log("[PixAlloy] resetToSplash called from:",new Error().stack?.split("\n")[2]?.trim());
    setShowSplash(true);
    if(eng.current){eng.current.destroy();eng.current=null;}
    if(bridgeRef.current){bridgeRef.current.destroy();bridgeRef.current=null;}sidebandRef.current=null;readbackViewRef.current=null;
    layerSyncRef.current=null;workerModeRef.current=false;
    selMaskRef.current=null;tfmBitmapRef.current=null;
    cvsCtxRef.current=null;ovlCtxRef.current=null;
    initCleanRef.current=false;dirtyRef.current=false;
    setSessionId(s=>s+1);
  },[]);

  // Keys — extracted to hooks/useKeyboard.js
  // Placed here because it depends on doExport and resetToSplash (defined above).
  useKeyboard({
    toolR, penUndoRef, penRedoRef, penPathRef, penDragRef, activePathIdxRef,
    bcolRef, bgColRef, dirtyRef,
    polyRef, sd, drag, eng,
    panState, rKeyHeldRef, spaceHeldRef, toolBeforeSpaceRef,
    penUndo, penRedo, setPenPathWithUndo, setPenPath, setPenPreset, setActivePathIdx,
    pc, fu, wp, setTool, rbActIdx, rbHasTfm,
    applyVPTransform, zoomToFit,
    doExport, doExportPSD, resetToSplash,
    setShowRulers, setSaveGuard, setShowText, setActiveMenu,
    setRadial, setSelCtxMenu, setAltHeld,
  });

  // F3 dev overlay toggle — separate from useKeyboard because it's UI-only
  useEffect(()=>{
    const h=e=>{if(e.key==="F3"){e.preventDefault();setShowDevOverlay(v=>!v);}};
    window.addEventListener("keydown",h);
    return()=>window.removeEventListener("keydown",h);
  },[]);

  const toggleSection=useCallback(name=>{setCollapsedSections(s=>({...s,[name]:!s[name]}));},[]);

  const startResize=useCallback((side,e)=>{e.preventDefault();const startX=e.clientX;const startW=side==="left"?leftW:rightW;const onM=e2=>{const delta=e2.clientX-startX;if(side==="left")setLeftW(Math.max(140,Math.min(400,startW+delta)));else setRightW(Math.max(180,Math.min(450,startW-delta)));};const onU=()=>{document.removeEventListener("mousemove",onM);document.removeEventListener("mouseup",onU);};document.addEventListener("mousemove",onM);document.addEventListener("mouseup",onU);},[leftW,rightW]);

  const E=eng.current;
  // In worker mode, eng.current is null — use layerSyncRef for layer panel data.
  // In single-thread mode, use eng.current.s.layers directly (has full canvas data for thumbnails).
  const layers=E?.s.layers||layerSyncRef.current||[];
  const actIdx=E?.s.act??bridgeRef.current?.getParam("activeLayer")??0;

  const penFlipH=()=>{const b=pathListBounds(penPathRef.current);if(!b)return;setPenPathWithUndo(transformPathList(penPathRef.current,(x,y)=>({x:b.cx+(b.cx-x),y})));};
  const penFlipV=()=>{const b=pathListBounds(penPathRef.current);if(!b)return;setPenPathWithUndo(transformPathList(penPathRef.current,(x,y)=>({x,y:b.cy+(b.cy-y)})));};
  const penRotate=(deg)=>{const b=pathListBounds(penPathRef.current);if(!b)return;const rad=deg*Math.PI/180;const cos=Math.cos(rad),sin=Math.sin(rad);
    setPenPathWithUndo(transformPathList(penPathRef.current,(x,y)=>{const dx=x-b.cx,dy=y-b.cy;return{x:b.cx+dx*cos-dy*sin,y:b.cy+dx*sin+dy*cos};}));};

  const cssFilter=`brightness(${1+bright/100}) contrast(${1+contrast/100}) saturate(${1+sat/100})`;
  const toolLabel=TOOLS.find(t=>t&&t.id===tool)?.l||"—";
  const history=E?.s.history||[];
  const zm=panState.current.zoom;

  const hslVal=rgbToHsl(curRGB.r,curRGB.g,curRGB.b);

  // ── RENDER ──
  // Viewport gate for small screens (1.8)
  const [viewportTooSmall, setViewportTooSmall] = useState(()=>typeof window!=="undefined"&&window.innerWidth<768);
  useEffect(()=>{
    const check=()=>setViewportTooSmall(window.innerWidth<768);
    window.addEventListener("resize",check);
    return()=>window.removeEventListener("resize",check);
  },[]);
  if(viewportTooSmall&&!mobileGateDismissed)return (
    <div style={{width:"100vw",height:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"#0d0d0d",color:"#ccc",fontFamily:"system-ui,sans-serif",padding:32,textAlign:"center",gap:16}}>
      <div style={{fontSize:32,marginBottom:8}}>🎨</div>
      <div style={{fontSize:18,fontWeight:600,color:"#fff"}}>PixAlloy Pro</div>
      <div style={{fontSize:13,maxWidth:320,lineHeight:1.6,color:"#999"}}>
        PixAlloy is designed for tablet and desktop screens. For the best experience, use a device with at least 768px width.
      </div>
      <button onClick={()=>{setMobileGateDismissed(true);try{sessionStorage.setItem("pixalloy_mobile_ok","1")}catch(e){}}}
        style={{marginTop:12,padding:"10px 24px",background:"#2563eb",color:"#fff",border:"none",borderRadius:6,fontSize:13,cursor:"pointer"}}>
        Continue anyway
      </button>
    </div>
  );

  if(showSplash)return (
    <Splash
      splashW={splashW} splashH={splashH}
      setSplashW={setSplashW} setSplashH={setSplashH}
      setShowSplash={setShowSplash}
      fiRef={fi} fiModeRef={fiModeRef}
      onFile={onFile} setPendingOpen={setPendingOpen}
    />
  );

  return (
    <div style={{width:"100vw",height:"100vh",display:"flex",flexDirection:"column",background:V.appBg,color:V.text,fontFamily:V.mono,fontSize:11,lineHeight:1.4,overflow:"hidden",userSelect:"none",WebkitFontSmoothing:"antialiased",cursor:busy?"wait":undefined}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;500;600;700&family=DM+Serif+Text:ital@0;1&display=swap');
        ::-webkit-scrollbar{width:4px;height:4px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:${V.panelBorder2};border-radius:2px}
        input[type=range]{height:4px;appearance:none;background:${V.panelBorder2};border-radius:2px;cursor:pointer;outline:none;width:100%}
        input[type=range]::-webkit-slider-thumb{appearance:none;width:12px;height:12px;background:${V.accentL};border-radius:50%;border:2px solid ${V.appBg};cursor:pointer;box-shadow:0 0 0 1px ${V.accent}}
        select{background:${V.panelActive};border:1px solid ${V.panelBorder2};color:${V.text};font-family:${V.mono};font-size:10px;padding:3px 6px;border-radius:3px;outline:none;cursor:pointer}
        .rh{width:4px;cursor:col-resize;background:transparent;transition:background 0.2s;flex-shrink:0}.rh:hover{background:${V.accent}44}
      `}</style>
      <input type="file" ref={fi} accept={ACCEPT} style={{display:"none"}} onChange={onFile}/>

      {/* ══ MENU BAR ══ */}
      {!isTablet && renderMenuBar({ menus: [
          {name:"File",items:[{l:"New…",s:"Ctrl+N",fn:()=>{if(dirtyRef.current){setSaveGuard({action:"new"});return;}resetToSplash();}},{l:"Open…",s:"Ctrl+O",fn:()=>{fiModeRef.current="open";fi.current?.click();}},{l:"Open as Layer…",fn:()=>{fiModeRef.current="layer";fi.current?.click();}},{l:"Import SVG/EPS…",fn:()=>{fiModeRef.current="open";fi.current?.click();}},"-",{l:"Export PNG",s:"Ctrl+E",fn:()=>doExport(EXPORTS[0])},{l:"Export JPEG",fn:()=>doExport(EXPORTS[1])},{l:"Export WebP",fn:()=>doExport(EXPORTS[2])},{l:"Export As…",fn:()=>setShowExportDialog(true)},{l:"Save as PSD",s:"Ctrl+⇧S",fn:doExportPSD},{l:"Export SVG (vector layers)",fn:()=>{
            const E2=eng.current;if(!E2)return;
            const vectorLayers=E2.s.layers.filter(l=>l.type==="vector"&&l.vectorData);
            if(vectorLayers.length===0){setToast({id:Date.now(),msg:"No vector layers to export",type:"error"});return;}
            const svgParts=vectorLayers.map(l=>pathListToSvg(l.vectorData.paths,l.vectorData.style,E2.s.w,E2.s.h));
            const blob=new Blob([svgParts[0]],{type:"image/svg+xml"});
            const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download="pixalloy-vector.svg";a.click();URL.revokeObjectURL(url);
          }}]},
          {name:"Edit",items:[{l:"Undo",s:"Ctrl+Z",fn:()=>{pc(C.UNDO);fu();}},{l:"Redo",s:"Ctrl+⇧Z",fn:()=>{pc(C.REDO);fu();}},"-",{l:"Copy",s:"Ctrl+C",fn:()=>pc(C.COPY)},{l:"Cut",s:"Ctrl+X",fn:()=>{pc(C.CUT);fu();}},{l:"Paste",s:"Ctrl+V",fn:()=>{pc(C.PASTE,{x:0,y:0});fu();}},"-",{l:"Duplicate Layer",s:"Ctrl+J",fn:()=>{pc(C.DUPLICATE_LAYER,{idx:rbActIdx()});fu();}},{l:"Clear Layer",s:"Del",fn:()=>{pc(C.LAYER_CLR);fu();}},{l:"Content-Aware Fill",fn:()=>{setBusy(true);pc(C.CONTENT_FILL);fu();requestAnimationFrame(()=>setBusy(false));}},"-",{l:"Flip Layer H",fn:()=>{pc(C.LAYER_FLIP_H);fu();}},{l:"Flip Layer V",fn:()=>{pc(C.LAYER_FLIP_V);fu();}},"-",{l:"Select Color Range…",fn:()=>{setColorRangeMode(true);}},"-",{l:"Actions…",fn:()=>setShowActionsDialog(true)}]},
          {name:"Select",items:[{l:"Select All",s:"Ctrl+A",fn:()=>pc(C.SEL_ALL)},{l:"Deselect",s:"Ctrl+D",fn:()=>pc(C.SEL_CLR)},{l:"Invert",s:"Ctrl+⇧I",fn:()=>{pc(C.SEL_INV);fu();}},{l:"Feather…",fn:()=>{if(featherR>0){pc(C.SEL_FEATHER,{radius:featherR});fu();}}},"-",{l:"Select by Color (Wand)",fn:()=>setTool(T.WAND)},{l:"Freehand Lasso",fn:()=>setTool(T.LASSO)},{l:"Polygon Lasso",fn:()=>setTool(T.POLY_LASSO)}]},
          {name:"Image",items:[{l:"Image Size…",fn:()=>setShowImgSize(true)},{l:"Canvas Size…",fn:()=>setShowCanvasSize(true)},"-",{l:"Flatten Image",fn:doFlatten},{l:"Flatten Layers",fn:()=>{pc(C.FLATTEN);fu();}},"-",{l:"Flip Horizontal",fn:()=>{pc(C.IMG_FLIP_H);fu();}},{l:"Flip Vertical",fn:()=>{pc(C.IMG_FLIP_V);fu();}},"-",{l:"Rotate 90° CW",fn:()=>{pc(C.IMG_ROT90);fu();}},{l:"Rotate 180°",fn:()=>{pc(C.IMG_ROT180);fu();}}]},
          {name:"Filter",items:[
            {header:"Adjustments"},{l:"Levels…",fn:()=>setShowLevels(true)},{l:"Hue/Saturation…",fn:()=>setShowHueSat(true)},{l:"Gaussian Blur…",fn:()=>setShowGaussBlur(true)},{l:"Color Balance…",fn:()=>setShowColorBal(true)},
            "-",{header:"Quick Filters"},...FILTERS.map(f=>({l:f.l,fn:()=>{pc(C.FILTER,{id:f.id});fu();}})),
            "-",{l:"Frequency Separation",fn:()=>{pc(C.FREQ_SEP,{radius:4});fu();}},
            "-",{header:"Develop"},{l:"Camera RAW / Develop…",fn:()=>setShowDevelop(true)},
            "-",{header:"Adjustment Layers"},
            {l:"⊕ Brightness/Contrast",fn:()=>{setAdjEditIdx(null);setAdjEditType(ADJ.BRIGHTNESS);setShowAdjDialog(true);}},
            {l:"⊕ Levels",fn:()=>{setAdjEditIdx(null);setAdjEditType(ADJ.LEVELS);setShowAdjDialog(true);}},
            {l:"⊕ Hue/Saturation",fn:()=>{setAdjEditIdx(null);setAdjEditType(ADJ.HUESAT);setShowAdjDialog(true);}},
            {l:"⊕ Color Balance",fn:()=>{setAdjEditIdx(null);setAdjEditType(ADJ.COLORBAL);setShowAdjDialog(true);}},
            {l:"⊕ Invert",fn:()=>{pc(C.ADJ_LAYER_ADD,{adjType:ADJ.INVERT,params:{}});fu();}},
            {l:"⊕ Curves",fn:()=>{setAdjEditIdx(null);setAdjEditType(ADJ.CURVES);setShowAdjDialog(true);}},
            "-",{header:"Color Grading"},
            {l:"⊕ Vibrance",fn:()=>{setAdjEditIdx(null);setAdjEditType(ADJ.VIBRANCE);setShowAdjDialog(true);}},
            {l:"⊕ Channel Mixer",fn:()=>{setAdjEditIdx(null);setAdjEditType(ADJ.CHANNEL_MIX);setShowAdjDialog(true);}},
            {l:"⊕ Black & White",fn:()=>{setAdjEditIdx(null);setAdjEditType(ADJ.BW_MIX);setShowAdjDialog(true);}},
            {l:"⊕ Selective Color",fn:()=>{setAdjEditIdx(null);setAdjEditType(ADJ.SELECTIVE);setShowAdjDialog(true);}},
            {l:"⊕ Gradient Map",fn:()=>{setAdjEditIdx(null);setAdjEditType(ADJ.GRAD_MAP);setShowAdjDialog(true);}},
            {l:"⊕ Shadow/Highlight",fn:()=>{setAdjEditIdx(null);setAdjEditType(ADJ.SHADOW_HL);setShowAdjDialog(true);}},
          ]},
          {name:"View",items:[{l:"Zoom to Fit",s:"Ctrl+0",fn:zoomToFit},{l:"Actual Size",s:"Ctrl+1",fn:()=>{panState.current.zoom=1;panState.current.panX=0;panState.current.panY=0;applyVPTransform();}},{l:"Zoom In",s:"Ctrl++",fn:()=>{panState.current.zoom=Math.min(panState.current.zoom*1.25,8);applyVPTransform();}},{l:"Zoom Out",s:"Ctrl+-",fn:()=>{panState.current.zoom=Math.max(panState.current.zoom/1.25,0.1);applyVPTransform();}},"-",{l:"Reset Rotation",fn:()=>{panState.current.rotation=0;applyVPTransform();setRotDisplay(0);}},{l:showRulers?"Hide Rulers":"Show Rulers",s:"Ctrl+R",fn:()=>setShowRulers(v=>!v)},{l:"Clear Guides",fn:()=>setGuides([])},{l:showClip?"Hide Clipping":"Show Clipping",fn:()=>setShowClip(v=>!v)},{l:leftCollapsed?"Show Left":"Hide Left",fn:()=>setLeftCollapsed(v=>!v)},{l:rightCollapsed?"Show Right":"Hide Right",fn:()=>setRightCollapsed(v=>!v)}]},
          {name:"Help",items:[{l:"About PixAlloy",fn:()=>{setShowAbout(true);setActiveMenu(null);}}]},
        ], activeMenu, setActiveMenu, showClip, fpsRef })}

      {/* Tablet: compact header bar with hamburger menu */}
      {isTablet && <div style={{height:36,background:V.panelBg,borderBottom:`1px solid ${V.panelBorder}`,display:"flex",alignItems:"center",padding:"0 10px",gap:8,flexShrink:0,position:"relative",zIndex:200}}>
        {/* Hamburger menu button */}
        <div onClick={()=>setActiveMenu(activeMenu==="tablet"?null:"tablet")} style={{width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:6,cursor:"pointer",background:activeMenu==="tablet"?V.panelActive:"transparent",color:activeMenu==="tablet"?V.text:V.muted,fontSize:16}}>☰</div>
        <span style={{color:V.accentL,fontWeight:600,fontSize:12}}>PixAlloy</span>
        <span style={{color:V.muted,fontSize:10}}>{toolLabel}</span>
        <div style={{flex:1}}/>
        {/* Quick action buttons */}
        <div onClick={()=>{fiModeRef.current="open";fi.current?.click();}} style={{padding:"4px 8px",borderRadius:4,cursor:"pointer",color:V.muted,fontSize:10,border:`1px solid ${V.panelBorder2}`}}>Open</div>
        <div onClick={()=>doExport(EXPORTS[0])} style={{padding:"4px 8px",borderRadius:4,cursor:"pointer",color:V.muted,fontSize:10,border:`1px solid ${V.panelBorder2}`}}>Export</div>
        <span style={{color:V.ghost,fontSize:9}}>{cw}×{ch} {zmDisplay}%</span>

        {/* Tablet hamburger dropdown — flat list of all menu sections */}
        {activeMenu==="tablet"&&<div style={{position:"absolute",top:"100%",left:0,right:0,maxHeight:"70vh",overflowY:"auto",background:V.panelRaised,border:`1px solid ${V.panelBorder2}`,borderTop:"none",boxShadow:"0 8px 32px rgba(0,0,0,0.6)",zIndex:999,padding:4}} onClick={e=>e.stopPropagation()}>
          {[
            {name:"File",items:[{l:"New…",fn:()=>{if(dirtyRef.current){setSaveGuard({action:"new"});return;}resetToSplash();}},{l:"Open…",fn:()=>{fiModeRef.current="open";fi.current?.click();}},{l:"Open as Layer…",fn:()=>{fiModeRef.current="layer";fi.current?.click();}},"-",{l:"Export PNG",fn:()=>doExport(EXPORTS[0])},{l:"Export JPEG",fn:()=>doExport(EXPORTS[1])},{l:"Save as PSD",fn:doExportPSD}]},
            {name:"Edit",items:[{l:"Undo",fn:()=>{pc(C.UNDO);fu();}},{l:"Redo",fn:()=>{pc(C.REDO);fu();}},"-",{l:"Copy",fn:()=>pc(C.COPY)},{l:"Cut",fn:()=>{pc(C.CUT);fu();}},{l:"Paste",fn:()=>{pc(C.PASTE,{x:0,y:0});fu();}},"-",{l:"Flatten",fn:()=>{pc(C.FLATTEN);fu();}}]},
            {name:"Image",items:[{l:"Image Size…",fn:()=>setShowImgSize(true)},{l:"Canvas Size…",fn:()=>setShowCanvasSize(true)},"-",{l:"Flip H",fn:()=>{pc(C.IMG_FLIP_H);fu();}},{l:"Flip V",fn:()=>{pc(C.IMG_FLIP_V);fu();}},{l:"Rotate 90°",fn:()=>{pc(C.IMG_ROT90);fu();}}]},
            {name:"Filter",items:[{l:"Levels…",fn:()=>setShowLevels(true)},{l:"Hue/Saturation…",fn:()=>setShowHueSat(true)},{l:"Gaussian Blur…",fn:()=>setShowGaussBlur(true)},{l:"Color Balance…",fn:()=>setShowColorBal(true)},"-",...FILTERS.map(f=>({l:f.l,fn:()=>{pc(C.FILTER,{id:f.id});fu();}}))]}
          ].map(section=>(
            <div key={section.name}>
              <div style={{padding:"6px 10px",fontSize:9,color:V.ghost,textTransform:"uppercase",letterSpacing:1}}>{section.name}</div>
              {section.items.map((item,i)=>item==="-"?(
                <div key={i} style={{height:1,background:V.panelBorder,margin:"2px 8px"}}/>
              ):(
                <div key={i} onClick={()=>{item.fn();setActiveMenu(null);}} style={{padding:"10px 14px",fontSize:12,color:V.text,cursor:"pointer",borderRadius:4,transition:"background 0.1s"}}
                  onMouseEnter={e=>e.currentTarget.style.background=V.panelHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>{item.l}</div>
              ))}
            </div>
          ))}
        </div>}
      </div>}

      {/* ══ OPTIONS BAR ══ */}
      {!isTablet && renderOptionsBar({ tool, toolLabel, bsz, setBsz, bop, setBop, bhard, setBhard, btip, setBtip,
        pressureSize, setPressureSize, pressureOpacity, setPressureOpacity,
        // Phase 3 dynamics
        brushSpacing, setBrushSpacing, brushFlow, setBrushFlow,
        sizeJitter, setSizeJitter, angleJitter, setAngleJitter,
        opacityJitter, setOpacityJitter, scatterAmount, setScatterAmount,
        brushSmoothing, setBrushSmoothing,
        pressureHardness, setPressureHardness, pressureFlow, setPressureFlow,
        pressureCurve, setPressureCurve,
        //
        gradMode, setGradMode, gradStops, setGradStops, bcol, bgCol,
        fillMode, setFillMode, fillCol2, setFillCol2, wandTol, setWandTol,
        eyedropSample, setEyedropSample,
        penPreset, setPenPreset, penPath, penPathRef, penStrokeSize, setPenStrokeSize, setPenPath,
        penFlipH, penFlipV, penRotate,
        featherR, setFeatherR, snapEnabled, setSnapEnabled,
        eng, readbackViewRef,
        pc, fu, doExport, setTool, actIdx })}


      {/* ══ APP BODY ══ */}
      <div style={{flex:1,display:"flex",overflow:"hidden",position:"relative"}} onClick={()=>{setActiveMenu(null);if(isTablet&&(responsive.leftOpen||responsive.rightOpen)){/* don't close on body click — backdrop handles it */}}}>

        {/* ── TOOLBAR (2-col grid) ── */}
        {!isTablet && renderToolbar({ tool, setTool, bcol, setBcol, bgCol, setBgCol, zoomToFit, panState, applyVPTransform, isTablet, pc, fu })}

        {/* ── LEFT PANEL ── */}
        {/* In tablet mode: overlay panel, only shown when toggled */}
        {(!isTablet || responsive.leftOpen) && <div style={isTablet ? {position:"absolute",left:0,top:0,bottom:90,zIndex:300,boxShadow:"4px 0 20px rgba(0,0,0,0.5)"} : {}}>
        {renderLeftPanel({ leftCollapsed: isTablet ? false : leftCollapsed, setLeftCollapsed: isTablet ? responsive.toggleLeft : setLeftCollapsed, leftW: isTablet ? 260 : leftW, startResize: isTablet ? null : startResize,
          navCvs, histCvs, histCh, setHistCh, waveCvs, showWave, setShowWave,
          collapsedSections, toggleSection,
          E, layers, actIdx, pc, fu,
          setAdjEditIdx, setAdjEditType, setShowAdjDialog,
          setTool, setBcol,
          cw, ch, curPos, curRGB, hslVal, bcol, bgCol,
          histJumpIdx, setHistJumpIdx, showClip, setShowClip,
          bright, setBright, contrast, setContrast, sat, setSat,
          history, zmDisplay, panState, applyVPTransform, zoomToFit,
          setShowLevels })}
        </div>}


        {/* ══ CANVAS AREA (includes status bar) ══ */}
        {/* Color Range selection mode banner */}
        {colorRangeMode && <div style={{position:"absolute",top:40,left:"50%",transform:"translateX(-50%)",zIndex:300,
          background:"rgba(37,99,235,0.9)",color:"#fff",padding:"8px 20px",borderRadius:8,fontSize:12,display:"flex",alignItems:"center",gap:12,boxShadow:"0 4px 20px rgba(0,0,0,0.4)"}}>
          <span>Click on a color to select similar pixels</span>
          <label style={{display:"flex",alignItems:"center",gap:4,fontSize:11}}>Fuzz:
            <input type="range" min={5} max={100} value={colorRangeFuzz} onChange={e=>setColorRangeFuzz(+e.target.value)} style={{width:60}}/>
            <span style={{minWidth:24}}>{colorRangeFuzz}</span>
          </label>
          <span onClick={()=>setColorRangeMode(false)} style={{cursor:"pointer",opacity:0.7,fontSize:14}}>✕</span>
        </div>}

        {/* Phase 9: Recording indicator */}
        {actionsHook.recording && <div style={{position:"absolute",top:40,right:20,zIndex:300,
          background:"rgba(30,30,30,0.95)",border:"1px solid #ef4444",padding:"8px 14px",borderRadius:8,
          fontSize:11,display:"flex",alignItems:"center",gap:8,boxShadow:"0 4px 20px rgba(0,0,0,0.5)"}}>
          <span style={{width:8,height:8,borderRadius:"50%",background:"#ef4444",display:"inline-block",animation:"pulse 1s infinite"}}/>
          <span style={{color:"#f87171"}}>Recording</span>
          <input type="text" placeholder="Action name" id="actionNameInput"
            style={{width:100,background:"#2a2a2a",border:"1px solid #444",borderRadius:4,padding:"3px 6px",fontSize:10,color:"#ccc",outline:"none"}}/>
          <span onClick={()=>{const n=document.getElementById("actionNameInput")?.value;actionsHook.stopRecord(n||undefined);}}
            style={{cursor:"pointer",padding:"3px 10px",background:"rgba(167,139,250,0.2)",border:"1px solid #a78bfa",borderRadius:4,color:"#a78bfa",fontSize:10}}>Stop & Save</span>
          <span onClick={actionsHook.cancelRecord}
            style={{cursor:"pointer",color:"#888",fontSize:10}}>Cancel</span>
        </div>}

        {renderCanvasArea({ showRulers: isTablet ? false : showRulers, panState, cw, ch, vpContainerRef, vpRef, cssFilter,
          cvs, cvsRefCb, ovlRefCb, sessionId,
          onDown, onMove, onUp, onLeave, onDblClick,
          guides, setGuides, nearGuide,
          toolLabel, curPos, zmDisplay, rotDisplay, wasmCaps, layers, E,
          tool, zm, altHeld, penHover, rbHasSel, tfmCursor,
          fi, guideDragRef, radial, setRadial, setSelCtxMenu })}

        {/* Tablet: panel toggle buttons on canvas edges */}
        {isTablet && !responsive.leftOpen && !responsive.rightOpen && <>
          <div onClick={responsive.toggleLeft} style={{position:"absolute",left:0,top:"50%",transform:"translateY(-50%)",width:24,height:64,background:V.panelBg+"cc",borderRadius:"0 8px 8px 0",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",zIndex:150,border:`1px solid ${V.panelBorder}`,borderLeft:"none"}}>
            <span style={{color:V.muted,fontSize:11}}>◂</span>
          </div>
          <div onClick={responsive.toggleRight} style={{position:"absolute",right:0,top:"50%",transform:"translateY(-50%)",width:24,height:64,background:V.panelBg+"cc",borderRadius:"8px 0 0 8px",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",zIndex:150,border:`1px solid ${V.panelBorder}`,borderRight:"none"}}>
            <span style={{color:V.muted,fontSize:11}}>▸</span>
          </div>
        </>}

        {/* Tablet: tap overlay backdrop to close panels */}
        {isTablet && (responsive.leftOpen || responsive.rightOpen) &&
          <div onClick={responsive.closeOverlays} style={{position:"absolute",inset:0,zIndex:250,background:"rgba(0,0,0,0.3)"}}/>}


        {/* ══ RIGHT PANEL ══ */}
        {(!isTablet || responsive.rightOpen) && <div style={isTablet ? {position:"absolute",right:0,top:0,bottom:90,zIndex:300,boxShadow:"-4px 0 20px rgba(0,0,0,0.5)"} : {}}>
        {renderRightPanel({ rightCollapsed: isTablet ? false : rightCollapsed, setRightCollapsed: isTablet ? responsive.toggleRight : setRightCollapsed, rightW: isTablet ? 280 : rightW, startResize: isTablet ? null : startResize,
          layers, actIdx, layerBlend, setLayerBlend, layerOp, setLayerOp,
          layerDrag, setLayerDrag, renamingLayer, setRenamingLayer, renameText, setRenameText,
          E, eng, workerModeRef, bridgeRef, sidebandRef,
          pc, fu, wp,
          setAdjEditIdx, setAdjEditType, setShowAdjDialog, setShowFx, setFxLayer,
          setGroupDelConfirm,
          tool, penPath, penPathRef, penPreset, penStrokeSize,
          activePathIdx: activePathIdx, setActivePathIdx,
          bsz, bop, bhard, btip, bcol, bgCol,
          bright, setBright, contrast, setContrast, sat, setSat,
          featherR,
          cw, ch, collapsedSections, toggleSection,
          penFlipH, penFlipV, setBsz, setBop, setBhard,
          setPenPath, setPenPreset, setPenStrokeSize, setGradStops, setTool,
          setShowVectorStyle, setVectorStyleLayer,
          onVectorSaveBack:(idx)=>{
            // Save current pen tool paths back to a vector layer
            const paths=penPathRef.current;
            if(paths.length>0&&idx!=null)pc(C.VECTOR_LAYER_UPDATE,{idx,paths:JSON.parse(JSON.stringify(paths))});fu();
          } })}
        </div>}

        {/* Tablet: bottom toolbar */}
        {isTablet && renderToolbar({ tool, setTool, bcol, setBcol, bgCol, setBgCol, zoomToFit, panState, applyVPTransform, isTablet, pc, fu,
          bsz, setBsz, bop, setBop, bhard, setBhard, btip, setBtip, brushFlow, setBrushFlow,
          onFilters:{
            levels:()=>setShowLevels(true),
            hueSat:()=>setShowHueSat(true),
            blur:()=>setShowGaussBlur(true),
            colorBal:()=>setShowColorBal(true),
            grayscale:()=>{pc(C.FILTER,{id:FLT.GRAY});fu();},
            invert:()=>{pc(C.FILTER,{id:FLT.INVERT});fu();},
            sepia:()=>{pc(C.FILTER,{id:FLT.SEPIA});fu();},
            sharpen:()=>{pc(C.FILTER,{id:FLT.SHARP});fu();},
            emboss:()=>{pc(C.FILTER,{id:FLT.EMBOSS});fu();},
            edge:()=>{pc(C.FILTER,{id:FLT.EDGE});fu();},
            posterize:()=>{pc(C.FILTER,{id:FLT.POSTERIZE});fu();},
            threshold:()=>{pc(C.FILTER,{id:FLT.THRESHOLD});fu();},
            chromatic:()=>{pc(C.FILTER,{id:FLT.CHROMATIC});fu();},
            pixelate:()=>{pc(C.FILTER,{id:FLT.PIXELATE});fu();},
          }})}
      </div>

      {/* Feature 4: Radial Context Menu */}
      {radial&&(<div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:500}} onClick={()=>setRadial(null)} onContextMenu={e=>e.preventDefault()}>
        <div style={{position:"absolute",left:radial.x-80,top:radial.y-80,width:160,height:160}}>
          <div style={{position:"absolute",left:60,top:60,width:40,height:40,borderRadius:"50%",background:V.panelRaised,border:`2px solid ${V.accent}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:V.accentL}}>⚡</div>
          {RADIAL_ITEMS.map(item=>{const rad2=item.a*Math.PI/180;const ix=80+Math.cos(rad2)*58-20;const iy=80+Math.sin(rad2)*58-14;
            return (<div key={item.id} onClick={e=>{e.stopPropagation();setTool(item.id);setRadial(null);}} style={{position:"absolute",left:ix,top:iy,width:40,height:28,borderRadius:4,background:tool===item.id?V.accentDim:V.panelRaised,border:`1px solid ${tool===item.id?V.accent:V.panelBorder2}`,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:9,color:tool===item.id?V.accentL:V.text,boxShadow:"0 2px 12px rgba(0,0,0,0.5)"}} onMouseEnter={e=>e.currentTarget.style.borderColor=V.accentL} onMouseLeave={e=>{if(tool!==item.id)e.currentTarget.style.borderColor=V.panelBorder2;}}>{item.k}</div>);
          })}
          {radial?.hasSel&&(<>
            {[{l:"Feather",fn:()=>{pc(C.SEL_FEATHER,{radius:5});fu();},y:130},{l:"Invert",fn:()=>{pc(C.SEL_INV);fu();},y:148}].map(si=>(<div key={si.l} onClick={e=>{e.stopPropagation();si.fn();setRadial(null);}} style={{position:"absolute",left:40,top:si.y,width:80,height:22,borderRadius:4,background:V.panelRaised,border:`1px solid ${V.panelBorder2}`,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:9,color:V.accentL,boxShadow:"0 2px 12px rgba(0,0,0,0.5)"}} onMouseEnter={e=>e.currentTarget.style.borderColor=V.accentL} onMouseLeave={e=>e.currentTarget.style.borderColor=V.panelBorder2}>{si.l}</div>))}
          </>)}
        </div>
      </div>)}

      {/* ══ SELECTION CONTEXT MENU (right-click with active selection) ══ */}
      {selCtxMenu&&(<div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:500}} onClick={()=>setSelCtxMenu(null)} onContextMenu={e=>e.preventDefault()}>
        <div onClick={e=>e.stopPropagation()} style={{position:"absolute",left:selCtxMenu.x,top:selCtxMenu.y,background:V.panelRaised,border:`1px solid ${V.panelBorder2}`,borderRadius:6,padding:"4px 0",minWidth:180,boxShadow:"0 8px 32px rgba(0,0,0,0.6)"}}>
          {[
            {l:"Copy",icon:"📋",fn:()=>{pc(C.COPY);}},
            {l:"Cut",icon:"✂",fn:()=>{pc(C.CUT);fu();}},
            {l:"Paste",icon:"📄",fn:()=>{pc(C.PASTE,{x:0,y:0});fu();}},
            null,
            {l:"Transform Selection",icon:"⬡",fn:()=>{setTool(T.TRANSFORM);pc(C.TFM_SEL_BEGIN);fu();}},
            {l:"Fill with FG Color",icon:"◼",fn:()=>{const{r,g,b}=hx(bcolRef.current);pc(C.FILL_COLOR,{r,g,b,label:"FG"});fu();}},
            {l:"Content-Aware Fill",icon:"✨",fn:()=>{setBusy(true);pc(C.CONTENT_FILL);fu();requestAnimationFrame(()=>setBusy(false));}},
            null,
            {l:"Selection → Path",icon:"✒",fn:()=>{
              // Convert selection mask to a pen path (marching squares → point list)
              const es=eng.current?.s;
              if(!es?.sel?.mask)return;
              const m=es.sel,mask=m.mask,sw=es.w;
              // Walk the selection boundary to generate a point sequence
              const pts=[];const visited=new Set();
              // Find first edge pixel
              let startX=-1,startY=-1;
              outer: for(let y=m.y;y<m.y+m.h;y++)for(let x=m.x;x<m.x+m.w;x++){
                if(mask[y*sw+x]>=128){const above=y>0?mask[(y-1)*sw+x]<128:true;
                  if(above){startX=x;startY=y;break outer;}}
              }
              if(startX<0)return;
              // Simple boundary trace (Moore neighborhood)
              const dirs=[[0,-1],[1,-1],[1,0],[1,1],[0,1],[-1,1],[-1,0],[-1,-1]];
              let cx2=startX,cy2=startY,dir=0,steps=0;
              const maxSteps=Math.max(es.w*es.h,50000);
              do{
                if(steps%3===0)pts.push({x:cx2,y:cy2,cp1x:null,cp1y:null,cp2x:null,cp2y:null});
                let found=false;
                for(let d=0;d<8;d++){
                  const nd=(dir+5+d)%8;const nx=cx2+dirs[nd][0],ny=cy2+dirs[nd][1];
                  if(nx>=0&&nx<es.w&&ny>=0&&ny<es.h&&mask[ny*sw+nx]>=128){
                    cx2=nx;cy2=ny;dir=nd;found=true;break;
                  }
                }
                if(!found)break;
                steps++;
              }while((cx2!==startX||cy2!==startY)&&steps<maxSteps);
              if(pts.length>=3){
                // Simplify: take every Nth point to reduce noise
                const simplified=pts.filter((_,i)=>i%(Math.max(1,Math.floor(pts.length/200)))===0);
                simplified.push({...simplified[0]});
                const path=mkPath(simplified.map(p2=>mkPt(p2.x,p2.y)),true,"From Selection");
                setPenPathWithUndo(pl=>[...pl,path]);
                setTool(T.PEN);
              }
            }},
            null,
            {l:"Select Inverse",icon:"⊘",fn:()=>{pc(C.SEL_INV);fu();}},
            {l:"Feather… (5px)",icon:"◎",fn:()=>{pc(C.SEL_FEATHER,{radius:5});fu();}},
            {l:"Feather… (10px)",icon:"◎",fn:()=>{pc(C.SEL_FEATHER,{radius:10});fu();}},
            null,
            {l:"Deselect",icon:"✕",fn:()=>{pc(C.SEL_CLR);fu();}},
          ].map((item,i)=>item===null?(
            <div key={"sep"+i} style={{height:1,background:V.panelBorder,margin:"3px 8px"}}/>
          ):(
            <div key={item.l} onClick={()=>{item.fn();setSelCtxMenu(null);}}
              style={{padding:"6px 14px",fontSize:11,color:V.text,cursor:"pointer",display:"flex",alignItems:"center",gap:8,transition:"background 0.1s"}}
              onMouseEnter={e=>e.currentTarget.style.background=V.panelHover}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <span style={{fontSize:12,width:16,textAlign:"center",color:V.muted}}>{item.icon}</span>
              <span>{item.l}</span>
            </div>
          ))}
        </div>
      </div>)}

      {/* ══ TOAST NOTIFICATION ══ */}
      {toast&&(<div key={toast.id} style={{position:"fixed",bottom:40,left:"50%",transform:"translateX(-50%)",padding:"10px 20px",borderRadius:8,background:toast.type==="error"?"#991b1b":"#1e3a5f",border:`1px solid ${toast.type==="error"?"#dc2626":"#3b82f6"}`,color:"#fff",fontSize:12,fontFamily:V.mono,zIndex:9999,boxShadow:"0 8px 32px rgba(0,0,0,0.5)",maxWidth:500,cursor:"pointer",animation:"splashFadeUp 0.3s ease-out"}} onClick={()=>setToast(null)}>{toast.msg}</div>)}

      {/* ══ F3 DEV OVERLAY ══ */}
      {showDevOverlay&&(()=>{const perf=bridgeRef.current?.getPerf?.();return(
        <div style={{position:"fixed",top:42,right:8,padding:"10px 14px",borderRadius:8,background:"rgba(0,0,0,0.85)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",fontSize:10,fontFamily:V.mono,zIndex:9999,lineHeight:1.8,minWidth:220,pointerEvents:"none"}}>
          <div style={{fontSize:11,fontWeight:600,color:"#fff",marginBottom:4}}>⚡ AlloyHead Perf</div>
          <div>FPS: <span style={{color:"#fff"}}>{fpsRef.current.fps||"--"}</span> · Tick: <span style={{color:"#fff"}}>{fpsRef.current.tickMs}ms</span></div>
          {perf&&<><div>Tick µs: <span style={{color:"#fff"}}>{Math.round(perf.tickDurationUs)}</span> · Cmds: <span style={{color:"#fff"}}>{perf.commandsDrained}</span></div>
          <div>Pixels: <span style={{color:"#fff"}}>{(perf.pixelsProcessed/1e6).toFixed(1)}M</span> · Buffers: <span style={{color:"#fff"}}>{perf.buffersMutated}</span></div></>}
          <div>Mode: <span style={{color:workerModeRef.current?"#60a5fa":"#fbbf24"}}>{workerModeRef.current?"Worker":"Single-thread"}</span></div>
          <div>Canvas: <span style={{color:"#fff"}}>{cw}×{ch}</span> · Layers: <span style={{color:"#fff"}}>{layers.length}</span></div>
          <div style={{fontSize:8,color:"rgba(255,255,255,0.3)",marginTop:4}}>Press F3 to hide</div>
        </div>);})()}

      {/* ══ DIALOGS ══ */}

      <TextDialog
        open={showText}
        onClose={()=>setShowText(false)}
        seed={textSeedRef}
        onCommit={cfg=>{
          const actL=eng.current?.s?.layers?.[eng.current?.s?.act];
          pc(actL?.textData?C.TEXT_UPDATE:C.TEXT_ADD,cfg);fu();
        }}
        activeTextData={eng.current?.s?.layers?.[eng.current?.s?.act]?.textData}
      />

      <FxDialog
        open={showFx}
        layerIdx={fxLayer}
        layerName={layers[fxLayer]?.name}
        engRef={eng}
        onCommit={(idx,fx)=>{pc(C.FX_UPDATE,{layerIdx:idx,fx});fu();}}
        onClose={()=>setShowFx(false)}
        forceUpdate={fu}
      />

      <AdjustmentDialog
        open={showAdjDialog}
        editIdx={adjEditIdx}
        editType={adjEditType}
        layers={layers}
        engRef={eng}
        onCommit={(type,params,idx)=>{
          if(idx!=null){pc(C.ADJ_LAYER_UPDATE,{idx,params});}
          else{pc(C.ADJ_LAYER_ADD,{adjType:type,params});}
          fu();
        }}
        onClose={()=>setShowAdjDialog(false)}
        forceUpdate={fu}
      />

      <LevelsDialog
        open={showLevels}
        onCommit={lvl=>{eng.current?.filterPreviewCommit("Levels");fu();setShowLevels(false);}}
        onClose={()=>setShowLevels(false)}
        onPreviewBegin={()=>eng.current?.filterPreviewBegin()}
        onPreview={(type,params)=>eng.current?.filterPreviewApply(type,params)}
        onPreviewCancel={()=>eng.current?.filterPreviewCancel()}
      />

      <HueSatDialog
        open={showHueSat}
        onCommit={p=>{eng.current?.filterPreviewCommit("Hue/Saturation");fu();setShowHueSat(false);}}
        onClose={()=>setShowHueSat(false)}
        onPreviewBegin={()=>eng.current?.filterPreviewBegin()}
        onPreview={(type,params)=>eng.current?.filterPreviewApply(type,params)}
        onPreviewCancel={()=>eng.current?.filterPreviewCancel()}
      />

      <GaussBlurDialog
        open={showGaussBlur}
        onCommit={radius=>{eng.current?.filterPreviewCommit("Gaussian Blur");fu();setShowGaussBlur(false);}}
        onClose={()=>setShowGaussBlur(false)}
        onPreviewBegin={()=>eng.current?.filterPreviewBegin()}
        onPreview={(type,params)=>eng.current?.filterPreviewApply(type,params)}
        onPreviewCancel={()=>eng.current?.filterPreviewCancel()}
      />

      <ColorBalDialog
        open={showColorBal}
        onCommit={p=>{eng.current?.filterPreviewCommit("Color Balance");fu();setShowColorBal(false);}}
        onClose={()=>setShowColorBal(false)}
        onPreviewBegin={()=>eng.current?.filterPreviewBegin()}
        onPreview={(type,params)=>eng.current?.filterPreviewApply(type,params)}
        onPreviewCancel={()=>eng.current?.filterPreviewCancel()}
      />

      <ImageSizeDialog
        open={showImgSize}
        currentW={cw} currentH={ch}
        onCommit={(w,h)=>{pc(C.IMG_RESIZE,{w,h});fu();}}
        onClose={()=>setShowImgSize(false)}
      />

      <CanvasSizeDialog
        open={showCanvasSize}
        currentW={cw} currentH={ch}
        onCommit={(w,h,anchor)=>{pc(C.CANVAS_SIZE,{w,h,anchor});fu();}}
        onClose={()=>setShowCanvasSize(false)}
      />

      <VectorStyleDialog
        open={showVectorStyle}
        layer={vectorStyleLayer!=null?layers[vectorStyleLayer]:null}
        layerIdx={vectorStyleLayer}
        onApply={(idx,style)=>{pc(C.VECTOR_LAYER_UPDATE,{idx,style});fu();}}
        onClose={()=>{setShowVectorStyle(false);setVectorStyleLayer(null);}}
      />

      <AboutDialog
        open={showAbout}
        onClose={()=>setShowAbout(false)}
      />

      <GroupDeleteDialog
        confirm={groupDelConfirm}
        onRelease={()=>{pc(C.LAYER_DEL,{idx:groupDelConfirm.idx,mode:"release"});fu();setGroupDelConfirm(null);}}
        onDeleteAll={()=>{pc(C.LAYER_DEL,{idx:groupDelConfirm.idx,mode:"delete"});fu();setGroupDelConfirm(null);}}
        onClose={()=>setGroupDelConfirm(null)}
      />

      <SaveGuardDialog
        guard={saveGuard}
        onExportAndContinue={()=>{
          doExport({l:"PNG",m:"image/png",e:"png"});
          const g=saveGuard;setSaveGuard(null);
          if(g.action==="new"){resetToSplash();}
          else if(g.action==="open"&&g.psdFile){doOpenPSD(g.psdFile);}
          else if(g.action==="open"&&g.bmp){doOpenBmp(g.bmp);}
        }}
        onDiscard={()=>{
          const g=saveGuard;setSaveGuard(null);
          if(g.action==="new"){resetToSplash();}
          else if(g.action==="open"&&g.psdFile){doOpenPSD(g.psdFile);}
          else if(g.action==="open"&&g.bmp){doOpenBmp(g.bmp);}
        }}
        onClose={()=>{if(saveGuard?.bmp?.close)saveGuard.bmp.close();setSaveGuard(null);}}
      />

      {/* Phase 9: Export Dialog */}
      <ExportDialog
        open={showExportDialog}
        canvasW={cw} canvasH={ch}
        onExport={doExportAdv}
        onClose={()=>setShowExportDialog(false)}
      />

      {/* Phase 10: Develop Dialog */}
      <DevelopDialog
        open={showDevelop}
        engRef={eng}
        onCommit={(devParams)=>{/* develop is applied destructively by the dialog's live preview */fu();}}
        onClose={()=>setShowDevelop(false)}
        forceUpdate={fu}
      />

      {/* Phase 9: Actions Dialog */}
      <ActionsDialog
        open={showActionsDialog}
        onClose={()=>setShowActionsDialog(false)}
        actions={actionsHook.actions}
        recording={actionsHook.recording}
        startRecord={actionsHook.startRecord}
        stopRecord={actionsHook.stopRecord}
        cancelRecord={actionsHook.cancelRecord}
        playAction={actionsHook.playAction}
        deleteAction={actionsHook.deleteAction}
        pc={pc} fu={fu}
      />

      {/* Phase 9: Auto-save recovery banner */}
      {autoSave.hasRecovery && (
        <div style={{position:"fixed",bottom:20,left:"50%",transform:"translateX(-50%)",zIndex:9999,
          background:"rgba(37,99,235,0.95)",color:"#fff",padding:"10px 20px",borderRadius:10,
          fontSize:12,display:"flex",alignItems:"center",gap:12,boxShadow:"0 4px 24px rgba(0,0,0,0.5)"}}>
          <span>Unsaved work from a previous session was found.</span>
          <span onClick={()=>{autoSave.doRecover();/* recovery data available via autoSave.recoverData */}}
            style={{cursor:"pointer",padding:"4px 12px",background:"rgba(255,255,255,0.2)",borderRadius:6,fontWeight:600}}>Recover</span>
          <span onClick={autoSave.dismissRecover}
            style={{cursor:"pointer",opacity:0.7}}>Dismiss</span>
        </div>
      )}
    </div>
  );
}
